/**
 * API Client
 *
 * Handles all communication with the Railway backend.
 * Uses Supabase JWT tokens for authentication.
 */

import { config } from './config';
import { getAccessToken } from './supabase';

const API_BASE = config.apiUrl;
const HENRY_API_BASE = config.henryApiUrl;

// Endpoints served by Henry's API (vs the general os-api backend)
const HENRY_PREFIXES = ['/api/chat', '/api/tools', '/api/commands'];
function getBaseUrl(endpoint: string): string {
  return HENRY_PREFIXES.some(p => endpoint.startsWith(p)) ? HENRY_API_BASE : API_BASE;
}

// =============================================================================
// Types
// =============================================================================

export interface ApiSession {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  is_archived: boolean;
  company_context?: string;
  // v6 metadata fields
  is_pinned?: boolean;
  folder_id?: string;
  last_activity_at?: string;
  client_tag?: string;
  fathom_meeting_count?: number;
  action_items_count?: number;
  project_tags?: string[];
  doc_links?: { title: string; url: string }[];
  api_call_count?: number;
}

export interface ApiFolder {
  id: string;
  name: string;
  parent_id?: string;
  color: string;
  icon: string;
  sort_order: number;
  created_at: string;
}

export interface ApiMessage {
  id: string;
  session_id: string;
  role: 'user' | 'assistant' | 'tool';
  content: string;
  tool_name?: string;
  tool_input?: Record<string, any>;
  tool_result?: any;
  created_at: string;
  // Token usage fields (persisted in DB)
  input_tokens?: number;
  output_tokens?: number;
  cost_usd?: number;
}

export interface ApiTool {
  name: string;
  display_name: string;
  description: string;
  permission_level: string;
  input_schema: Record<string, any>;
}

export interface ChatRequest {
  message: string;
  session_id?: string;
  company_context?: string;
}

export interface SSEEvent {
  type: 'message_start' | 'content_delta' | 'tool_call' | 'tool_progress' | 'tool_result' | 'message_end' | 'error' | 'processing' | 'ping' | 'api_call';
  data: any;
}

// Token usage from message_end event
export interface TokenUsage {
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
}

// =============================================================================
// Helper Functions
// =============================================================================

async function getAuthHeaders(skipContentType = false): Promise<HeadersInit> {
  const token = await getAccessToken();
  const headers: HeadersInit = {};

  // Only set Content-Type for JSON requests (not for FormData)
  if (!skipContentType) {
    headers['Content-Type'] = 'application/json';
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
}

export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  // Skip Content-Type header for FormData (browser sets multipart/form-data automatically)
  const isFormData = options.body instanceof FormData;
  const headers = await getAuthHeaders(isFormData);

  const response = await fetch(`${getBaseUrl(endpoint)}${endpoint}`, {
    ...options,
    credentials: 'include',  // Required for CORS with credentials
    headers: {
      ...headers,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.detail || error.error || `API error: ${response.status}`);
  }

  return response.json();
}

/**
 * API Client object with convenience methods for common HTTP operations.
 */
export const apiClient = {
  get: <T>(endpoint: string) => apiRequest<T>(endpoint),
  post: <T>(endpoint: string, data?: unknown) =>
    apiRequest<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }),
  put: <T>(endpoint: string, data?: unknown) =>
    apiRequest<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    }),
  delete: <T>(endpoint: string) => apiRequest<T>(endpoint, { method: 'DELETE' }),
};

// =============================================================================
// Health Check
// =============================================================================

export async function checkHealth(): Promise<{ status: string; service: string; model: string }> {
  return apiRequest('/api/health');
}

// =============================================================================
// Sessions
// =============================================================================

export async function createSession(title?: string, companyContext?: string): Promise<ApiSession> {
  return apiRequest('/api/chat/sessions', {
    method: 'POST',
    body: JSON.stringify({ title, company_context: companyContext }),
  });
}

export async function getSessions(limit = 50, offset = 0): Promise<{ sessions: ApiSession[] }> {
  return apiRequest(`/api/chat/sessions?limit=${limit}&offset=${offset}`);
}

export async function getSessionMessages(
  sessionId: string,
  limit = 100
): Promise<{ messages: ApiMessage[] }> {
  return apiRequest(`/api/chat/sessions/${sessionId}/messages?limit=${limit}`);
}

export async function updateSession(
  sessionId: string,
  updates: { title?: string }
): Promise<ApiSession> {
  return apiRequest(`/api/chat/sessions/${sessionId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

export async function archiveSession(sessionId: string): Promise<void> {
  return apiRequest(`/api/chat/sessions/${sessionId}`, {
    method: 'DELETE',
  });
}

export async function bulkArchiveSessions(sessionIds: string[]): Promise<{ archived_count: number }> {
  return apiRequest('/api/chat/sessions/bulk-archive', {
    method: 'POST',
    body: JSON.stringify({ session_ids: sessionIds }),
  });
}

// =============================================================================
// Session Pin/Folder/Metadata (v6)
// =============================================================================

export async function toggleSessionPin(sessionId: string): Promise<{ is_pinned: boolean }> {
  return apiRequest(`/api/chat/sessions/${sessionId}/pin`, {
    method: 'PATCH',
  });
}

export async function moveSessionToFolder(
  sessionId: string,
  folderId: string | null
): Promise<{ folder_id: string | null }> {
  return apiRequest(`/api/chat/sessions/${sessionId}/folder?folder_id=${folderId || ''}`, {
    method: 'PATCH',
  });
}

export async function updateSessionMetadata(
  sessionId: string,
  metadata: { client_tag?: string; project_tags?: string[]; doc_links?: { title: string; url: string }[] }
): Promise<ApiSession> {
  return apiRequest(`/api/chat/sessions/${sessionId}/metadata`, {
    method: 'PATCH',
    body: JSON.stringify(metadata),
  });
}

// =============================================================================
// Semantic Search
// =============================================================================

export interface ChatSearchResult {
  id: string;
  session_id: string;
  session_title: string;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
  similarity: number;
}

export interface ChatSearchResponse {
  results: ChatSearchResult[];
  query: string;
  search_type?: 'semantic' | 'text';
  error?: string;
}

/**
 * Search across all chat history using semantic AI search.
 * Finds messages that are semantically similar to the query.
 */
export async function searchChatMessages(
  query: string,
  limit = 20
): Promise<ChatSearchResponse> {
  return apiRequest('/api/chat/search', {
    method: 'POST',
    body: JSON.stringify({ query, limit }),
  });
}

// =============================================================================
// Folders (v6)
// =============================================================================

export async function getFolders(): Promise<{ folders: ApiFolder[] }> {
  return apiRequest('/api/chat/folders');
}

export async function createFolder(data: {
  name: string;
  parent_id?: string;
  color?: string;
  icon?: string;
}): Promise<ApiFolder> {
  return apiRequest('/api/chat/folders', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateFolder(
  folderId: string,
  updates: { name?: string; parent_id?: string; color?: string; icon?: string; sort_order?: number }
): Promise<ApiFolder> {
  return apiRequest(`/api/chat/folders/${folderId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

export async function deleteFolder(folderId: string): Promise<{ success: boolean }> {
  return apiRequest(`/api/chat/folders/${folderId}`, {
    method: 'DELETE',
  });
}

// =============================================================================
// Chat (Streaming)
// =============================================================================

// Default timeout for chat requests (10 minutes for long-running operations like client profiles)
const CHAT_TIMEOUT_MS = 600000;

// Track active request for cancellation
let activeAbortController: AbortController | null = null;

export function cancelActiveRequest() {
  if (activeAbortController) {
    activeAbortController.abort();
    activeAbortController = null;
  }
}

export async function* sendChatMessage(
  request: ChatRequest,
  timeoutMs: number = CHAT_TIMEOUT_MS
): AsyncGenerator<SSEEvent, void, unknown> {
  // Cancel any existing request
  cancelActiveRequest();

  const token = await getAccessToken();
  const abortController = new AbortController();
  activeAbortController = abortController;

  // Set up timeout
  const timeoutId = setTimeout(() => {
    abortController.abort();
  }, timeoutMs);

  let response: Response;
  try {
    response = await fetch(`${HENRY_API_BASE}/api/chat`, {
      method: 'POST',
      credentials: 'include',  // Required for CORS with credentials
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(request),
      signal: abortController.signal,
    });
  } catch (networkError) {
    clearTimeout(timeoutId);
    activeAbortController = null;
    if (networkError instanceof Error && networkError.name === 'AbortError') {
      throw new Error('Request timed out or was cancelled. Please try again.');
    }
    throw new Error(`Network error: Unable to connect to ${HENRY_API_BASE}. Please check your connection.`);
  }

  if (!response.ok) {
    clearTimeout(timeoutId);
    activeAbortController = null;
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.detail || error.error || `API error: ${response.status}`);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    clearTimeout(timeoutId);
    activeAbortController = null;
    throw new Error('No response body from server');
  }

  const decoder = new TextDecoder();
  let buffer = '';
  let hasReceivedContent = false;
  let lastActivityTime = Date.now();

  // Idle timeout - if no data received for 5 minutes, abort
  // This needs to be long enough to handle tool executions (founder_brain can take 3+ minutes)
  // The backend sends periodic progress events every 15 seconds during long-running tool executions
  const IDLE_TIMEOUT_MS = 300000;
  const idleCheckInterval = setInterval(() => {
    if (Date.now() - lastActivityTime > IDLE_TIMEOUT_MS) {
      console.warn('Stream idle timeout - no data received for 5 minutes');
      abortController.abort();
    }
  }, 10000);

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        // Stream ended - if we never got content, something went wrong
        if (!hasReceivedContent) {
          console.warn('SSE stream ended without content');
        }
        break;
      }

      // Update activity timestamp
      lastActivityTime = Date.now();

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      // Parse SSE format: event: type\ndata: json\n\n
      let currentEventType: string | null = null;

      for (const line of lines) {
        // Event type line
        if (line.startsWith('event: ')) {
          currentEventType = line.slice(7).trim();
          continue;
        }

        // Data line
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          if (data === '[DONE]') return;

          try {
            const parsedData = JSON.parse(data);
            // Construct SSEEvent with type from event: line or from data
            const event: SSEEvent = {
              type: (currentEventType || parsedData.type || 'unknown') as SSEEvent['type'],
              data: parsedData
            };
            hasReceivedContent = true;
            yield event;
          } catch (e) {
            console.warn('Failed to parse SSE event:', data, 'type:', currentEventType);
          }
          // Reset event type after processing
          currentEventType = null;
        }
      }
    }
  } catch (readError) {
    if (readError instanceof Error && readError.name === 'AbortError') {
      throw new Error('Request timed out or was cancelled. Please try again.');
    }
    throw new Error(`Stream error: Connection lost while receiving response. ${readError instanceof Error ? readError.message : ''}`);
  } finally {
    clearTimeout(timeoutId);
    clearInterval(idleCheckInterval);
    activeAbortController = null;
    reader.releaseLock();
  }
}

// =============================================================================
// Tools
// =============================================================================

export async function getTools(): Promise<{ tools: ApiTool[] }> {
  return apiRequest('/api/tools');
}

// =============================================================================
// Commands (for slash command menu)
// =============================================================================

export interface ApiCommand {
  id: string;
  name: string;
  description: string;
  example: string;
  displayName: string;
  type: 'company' | 'tool' | 'directive' | 'client';
  executionType: 'context' | 'major';
  contextPath?: string;
  toolName?: string;
  clientSlug?: string;
  estimatedTime?: string;
}

export async function getCommands(): Promise<{ commands: ApiCommand[]; total: number }> {
  return apiRequest('/api/commands');
}

export interface CommandUpdateRequest {
  displayName?: string;
  description?: string;
  example?: string;
  isHidden?: boolean;
}

export interface CommandOverride {
  command_id: string;
  display_name?: string;
  description?: string;
  example?: string;
  is_hidden?: boolean;
}

export async function updateCommandOverride(
  commandId: string,
  updates: CommandUpdateRequest
): Promise<{ success: boolean; command_id: string; override: CommandOverride }> {
  return apiRequest(`/api/commands/${commandId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

export async function deleteCommandOverride(
  commandId: string
): Promise<{ success: boolean; command_id: string; message: string }> {
  return apiRequest(`/api/commands/${commandId}/override`, {
    method: 'DELETE',
  });
}

// =============================================================================
// Admin Endpoints
// =============================================================================

export interface AdminUser {
  id: string;
  email: string;
  name?: string;  // For bootstrap compatibility
  display_name?: string;
  role: 'owner' | 'admin' | 'staff' | 'readonly' | 'manager' | string;
  status?: 'active' | 'inactive' | string;
  is_employee?: boolean;  // Whether this user is an employee (true) or contractor/external (false)
  department?: string;
  permissions?: {
    crm?: { read: boolean; create: boolean; update: boolean; delete: boolean };
    analytics?: { read: boolean };
    chat?: { read: boolean };
    admin?: { read: boolean; manage_users: boolean; manage_settings: boolean };
    projects?: { read: boolean; create: boolean; update: boolean; delete: boolean };
    hiring?: { read: boolean; create: boolean; update: boolean; delete: boolean };
    forms?: { read: boolean; create: boolean; update: boolean; delete: boolean };
    portal?: { read: boolean; manage: boolean };
    team_hub?: { read: boolean; create: boolean; update: boolean; delete: boolean };
    customer_success?: { read: boolean; manage: boolean };
    production?: { read: boolean; create: boolean; update: boolean; delete: boolean };
    agency_intel?: { read: boolean };
  };
  usage_limit_tokens_monthly?: number;
  monthly_tokens_used?: number;
  monthly_tokens_remaining?: number;
  created_at?: string;
  updated_at?: string;
}

export interface UsageStats {
  period: string;
  start_date: string;
  total_input_tokens: number;
  total_output_tokens: number;
  total_tokens: number;
  total_cost_usd: number;
  chat_count: number;
  tool_call_count: number;
  unique_users: number;
  by_user: Record<string, { input_tokens: number; output_tokens: number; cost_usd: number }>;
}

export interface ToolUsageStats {
  period: string;
  tools: {
    tool_name: string;
    executions: number;
    success_count: number;
    failure_count: number;
    avg_execution_time_ms: number;
    unique_users: number;
  }[];
  total_executions: number;
}

export async function getAdminUsers(): Promise<{ users: AdminUser[]; total: number }> {
  return apiRequest('/api/admin/users');
}

export async function getCurrentUserProfile(): Promise<AdminUser> {
  return apiRequest('/api/chat/me');
}

export async function updateAdminUser(
  userId: string,
  updates: {
    role?: string;
    status?: string;
    display_name?: string;
    usage_limit_tokens_monthly?: number;
    permissions?: AdminUser['permissions'];
    is_employee?: boolean; // Deprecated, use user_type
    user_type?: 'employee' | 'client';
  }
): Promise<AdminUser> {
  return apiRequest(`/api/admin/users/${userId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

export async function getPermissionSchema(): Promise<{
  schema: Record<string, { label: string; description: string; actions: Record<string, string> }>;
  features: string[];
}> {
  return apiRequest('/api/admin/permissions/schema');
}

export async function getAdminUsageStats(
  period: 'day' | 'week' | 'month' = 'month'
): Promise<UsageStats> {
  return apiRequest(`/api/admin/usage?period=${period}`);
}

export async function getAdminToolUsage(
  period: 'day' | 'week' | 'month' = 'month'
): Promise<ToolUsageStats> {
  return apiRequest(`/api/admin/tool-usage?period=${period}`);
}

export async function getAdminConversations(
  limit = 50,
  offset = 0
): Promise<{ conversations: ApiSession[] }> {
  return apiRequest(`/api/admin/conversations?limit=${limit}&offset=${offset}`);
}

export async function getAdminConversationMessages(
  sessionId: string
): Promise<{ session: ApiSession; messages: ApiMessage[] }> {
  return apiRequest(`/api/admin/conversations/${sessionId}/messages`);
}

// =============================================================================
// Session Title Generation
// =============================================================================

export interface GenerateTitleResponse {
  title: string;
  generated: boolean;
  error?: string;
}

export async function generateSessionTitle(
  sessionId: string,
  userMessage: string,
  assistantResponse: string
): Promise<GenerateTitleResponse> {
  return apiRequest(`/api/chat/sessions/${sessionId}/generate-title`, {
    method: 'POST',
    body: JSON.stringify({
      user_message: userMessage,
      assistant_response: assistantResponse,
    }),
  });
}

// =============================================================================
// Artifact Metadata Generation
// =============================================================================

export interface ArtifactMetadataResponse {
  displayTitle: string;  // 5 words or less
  description: string;   // One sentence overview
  generated: boolean;
  error?: string;
}

export async function generateArtifactMetadata(
  content: string
): Promise<ArtifactMetadataResponse> {
  return apiRequest('/api/chat/artifact-metadata', {
    method: 'POST',
    body: JSON.stringify({
      content: content.slice(0, 3000), // Send first 3000 chars for context
    }),
  });
}

// =============================================================================
// Templates (Two-Tier System)
// =============================================================================

export interface ApiTemplate {
  id: string;
  name: string;
  description: string;
  prefill_text?: string;
  suggested_commands: string[];
  is_global: boolean;
  user_id?: string;
  is_proposed: boolean;
  proposed_at?: string;
  created_at: string;
  updated_at: string;
  owner_email?: string;
}

export interface TemplatesListResponse {
  global_templates: ApiTemplate[];
  personal_templates: ApiTemplate[];
  // Admin-only fields
  proposed_templates?: ApiTemplate[];
  all_users_templates?: Record<string, ApiTemplate[]>;
}

export interface TemplateCreateRequest {
  name: string;
  description: string;
  prefill_text?: string;
  suggested_commands: string[];
  is_global?: boolean;
}

export interface TemplateUpdateRequest {
  name?: string;
  description?: string;
  prefill_text?: string;
  suggested_commands?: string[];
}

export async function getTemplates(): Promise<TemplatesListResponse> {
  return apiRequest('/api/templates');
}

export async function createTemplate(
  request: TemplateCreateRequest
): Promise<ApiTemplate> {
  return apiRequest('/api/templates', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export async function updateTemplate(
  templateId: string,
  request: TemplateUpdateRequest
): Promise<ApiTemplate> {
  return apiRequest(`/api/templates/${templateId}`, {
    method: 'PUT',
    body: JSON.stringify(request),
  });
}

export async function deleteTemplate(
  templateId: string
): Promise<{ success: boolean; deleted_id: string }> {
  return apiRequest(`/api/templates/${templateId}`, {
    method: 'DELETE',
  });
}

export async function proposeTemplate(
  templateId: string
): Promise<{ success: boolean; message: string; template: ApiTemplate }> {
  return apiRequest(`/api/templates/${templateId}/propose`, {
    method: 'POST',
  });
}

export async function approveTemplate(
  templateId: string
): Promise<{ success: boolean; message: string; global_template: ApiTemplate }> {
  return apiRequest(`/api/templates/${templateId}/approve`, {
    method: 'POST',
  });
}

export async function rejectTemplate(
  templateId: string
): Promise<{ success: boolean; message: string; template: ApiTemplate }> {
  return apiRequest(`/api/templates/${templateId}/reject`, {
    method: 'POST',
  });
}

// =============================================================================
// Contacts / CRM
// =============================================================================

import type {
  Contact,
  ContactSearchRequest,
  ContactSearchResponse,
  ContactCreateRequest,
  ContactAssociatedResponse,
  ContactEmailsResponse,
  ContactMeetingsResponse,
  CRMStats,
} from '../types/contacts';

export async function searchContacts(
  request: ContactSearchRequest
): Promise<ContactSearchResponse> {
  return apiRequest('/api/contacts/search', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export async function getContact(contactId: string): Promise<Contact> {
  return apiRequest(`/api/contacts/${contactId}`);
}

export async function getContactAssociated(contactId: string): Promise<ContactAssociatedResponse> {
  return apiRequest(`/api/contacts/${contactId}/associated`);
}

export async function getContactEmails(contactId: string, limit: number = 10): Promise<ContactEmailsResponse> {
  return apiRequest(`/api/contacts/${contactId}/emails?limit=${limit}`);
}

export async function getContactMeetings(
  contactId: string,
  upcoming: boolean = true,
  limit: number = 10
): Promise<ContactMeetingsResponse> {
  return apiRequest(`/api/contacts/${contactId}/meetings?upcoming=${upcoming}&limit=${limit}`);
}

export async function createContact(
  request: ContactCreateRequest
): Promise<Contact> {
  return apiRequest('/api/contacts', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export async function updateContact(
  contactId: string,
  request: Partial<ContactCreateRequest>
): Promise<Contact> {
  return apiRequest(`/api/contacts/${contactId}`, {
    method: 'PATCH',
    body: JSON.stringify(request),
  });
}

export async function getCRMStats(): Promise<CRMStats> {
  return apiRequest('/api/contacts/stats/overview');
}

// Enrich contact with external data (email, phone, LinkedIn, etc.)
export interface EnrichContactResponse {
  contact_id: string;
  status: 'queued' | 'enriched' | 'failed' | 'already_enriched';
  enriched_at: string | null;
  enriched_fields: string[] | null;
  message: string | null;
}

export async function enrichContact(
  contactId: string,
  options?: { skip_email?: boolean; skip_phone?: boolean }
): Promise<EnrichContactResponse> {
  const params = new URLSearchParams();
  if (options?.skip_email) params.set('skip_email', 'true');
  if (options?.skip_phone) params.set('skip_phone', 'true');
  const queryString = params.toString();
  return apiRequest(`/api/contacts/${contactId}/enrich${queryString ? `?${queryString}` : ''}`, {
    method: 'POST',
  });
}

// =============================================================================
// Semantic Search (AI-Powered CRM Search)
// =============================================================================

export interface SemanticSearchFilter {
  key: string;
  value: any;
  label: string;
  active: boolean;  // true = enabled, false = grayed out
}

export interface AIFilterSuggestion {
  term: string;
  contact_count: number;
  description: string;
  enabled: boolean;  // User has toggled this on
}

export interface SemanticSearchRequest {
  query: string;
  include_brands: boolean;
  active_filters: Record<string, any>;
  disabled_filters: Record<string, any>;
  included_suggestions: string[];
  limit?: number;
  offset?: number;
}

export interface ContactWithSource {
  id: string;
  contact_type: 'person' | 'company';
  full_name: string | null;
  company_name: string | null;
  job_title: string | null;
  email: string | null;
  phone: string | null;
  linkedin_url: string | null;
  city: string | null;
  state: string | null;
  // Origin context - critical for display
  origin_summary: string | null;  // 1-2 sentence overview
  origin_article_url: string | null;  // Link to source article
  origin_article_headline: string | null;
  origin_article_date: string | null;
  // Signals
  signal_types: string[];
  signal_articles: { article_id: string; title: string; url: string; signal_type: string; date: string }[];
  source_tags: string[];
  // D100
  d100_status: string | null;
  d100_priority: string | null;
  d100_portfolio: string | null;
  // Enrichment
  enriched_at: string | null;
}

export interface SemanticSearchResponse {
  query: string;
  detected_filters: Record<string, any>;
  suggestions: AIFilterSuggestion[];
  total_results: number;
  contacts: ContactWithSource[];
}

export async function semanticSearchContacts(
  request: SemanticSearchRequest
): Promise<SemanticSearchResponse> {
  return apiRequest('/api/contacts/semantic-search', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

// =============================================================================
// Admin Dashboard Types & API
// =============================================================================

export interface DashboardOverview {
  totalSessions: number;
  totalMessages: number;
  activeUsers: number;
  commandsUsed: number;
  weeklyActivity: { day: string; sessions: number; messages: number }[];
  topCommands: { command: string; count: number }[];
  avgSessionDuration: string;
  messagesPerSession: number;
  commandsPerMessage: number;
  changeFromLastWeek: {
    sessions: number;
    messages: number;
    users: number;
    commands: number;
  };
}

export interface EmployeeHealth {
  user_id: string;
  name: string;
  email: string;
  department: string;
  role: string;
  is_employee: boolean;  // Whether this user is an employee (true) or contractor/external (false)
  projects_owned: number;
  projects_contributing: number;
  project_names: string[];
  total_tasks: number;
  tasks_completed: number;
  tasks_in_progress: number;
  tasks_pending: number;
  tasks_blocked: number;
  overdue: number;
  due_today: number;
  due_this_week: number;
  due_next_week: number;
  health_score: number;
  health_status: 'critical' | 'warning' | 'good' | 'excellent';
  last_activity_at: string | null;
  chat_sessions_this_week: number;
  messages_this_week: number;
}

export interface TeamWorkload {
  workload: {
    owner: string;
    department: string;
    total_tasks: number;
    completed: number;
    in_progress: number;
    pending: number;
    blocked: number;
    overdue: number;
    due_today: number;
    due_this_week: number;
  }[];
  by_department: Record<string, { total: number; overdue: number; blocked: number }>;
  total_overdue: number;
  total_blocked: number;
  total_due_today: number;
}

export interface ProjectSummary {
  id: string;
  name: string;
  slug: string;
  status: string;
  is_internal: boolean;
  company: string | null;
  client: string | null;
  department: string;
  total_tasks: number;
  completed_tasks: number;
  active_tasks: number;
  recent_contributors: string[];
  last_activity_at: string | null;
  created_at: string;
}

export interface ProjectsSummary {
  projects: ProjectSummary[];
  total: number;
  by_status: Record<string, number>;
  by_department: Record<string, number>;
}

export interface IntelligenceHealth {
  news_intel: {
    status: 'healthy' | 'warning' | 'error';
    articles_today: number;
    high_priority_signals: number;
    total_this_week: number;
    brands_mentioned: number;
    last_run: string | null;
  };
  social_intel: {
    status: 'healthy' | 'warning' | 'error';
    profiles_monitored: number;
    posts_scraped_today: number;
    engagement_signals: number;
    keywords_tracked: number;
    last_run: string | null;
  };
  meetings: {
    status: 'healthy' | 'warning' | 'error';
    meetings_this_week: number;
    action_items_extracted: number;
    pending_triage: number;
    ideas_captured: number;
    last_run: string | null;
  };
  d100: {
    status: 'healthy' | 'warning' | 'error';
    total_prospects: number;
    contacted: number;
    qualified: number;
    conversion_rate: number;
    by_status: Record<string, number>;
    last_updated: string | null;
  };
}

export interface CostSummary {
  total_llm_cost: number;
  total_api_cost: number;
  change_from_prior_period: number;
  by_api: Record<string, number>;
  by_user: { user_id: string; name: string; cost: number }[];
  daily_trend: { date: string; cost: number }[];
  by_model?: Record<string, { calls: number; cost: number; input_tokens: number; output_tokens: number }>;
  claude_code?: {
    sessions: number;
    estimated_api_cost: number;
    total_messages: number;
    by_model: Record<string, number>;
    by_type: Record<string, number>;
    note: string;
  };
}

export interface MasterCostSummary {
  total_cost: number;
  change_from_prior_period: number;
  by_system: Record<string, { cost: number; calls: number; tokens: number; input_tokens: number; output_tokens: number }>;
  by_provider: Record<string, { cost: number; calls: number; input_tokens: number; output_tokens: number }>;
  by_feature: { feature: string; cost: number; calls: number; top_provider: string; input_tokens: number; output_tokens: number }[];
  by_company: Record<string, number>;
  by_user: { user_id: string; name: string; cost: number; calls: number }[];
  by_model: Record<string, { cost: number; calls: number; input_tokens: number; output_tokens: number }>;
  daily_trend: { date: string; agencyos: number; henry: number; ai_dev_platform: number; total: number }[];
  budgets: CostBudget[];
  alerts: CostAlert[];
  claude_code?: {
    sessions: number;
    estimated_api_cost: number;
    total_messages: number;
    input_tokens: number;
    output_tokens: number;
    cache_read_tokens: number;
    by_model: Record<string, number>;
    by_type: Record<string, number>;
    note: string;
  };
}

export interface CostBudget {
  id: string;
  name: string;
  scope: string;
  scope_value: string | null;
  period: string;
  budget_amount_usd: number;
  alert_threshold_pct: number;
  is_active: boolean;
  current_spend_usd?: number;
  percent_used?: number;
  status?: 'ok' | 'warning' | 'critical';
}

export interface CostAlert {
  id: string;
  budget_id: string;
  alert_type: string;
  message: string;
  current_spend_usd: number;
  percent_used: number;
  acknowledged: boolean;
  created_at: string;
}

// Dashboard API Functions
export async function getDashboardOverview(): Promise<DashboardOverview> {
  return apiRequest('/api/admin/dashboard/overview');
}

export async function getTopCommandsForDate(
  date?: string
): Promise<{ topCommands: { command: string; count: number }[]; date: string | null }> {
  const query = date ? `?date=${date}` : '';
  return apiRequest(`/api/admin/dashboard/top-commands${query}`);
}

export async function getEmployeeHealth(): Promise<{ employees: EmployeeHealth[] }> {
  return apiRequest('/api/admin/dashboard/employee-health');
}

export async function getTeamWorkload(): Promise<TeamWorkload> {
  return apiRequest('/api/admin/dashboard/workload');
}

export async function getProjectsSummary(): Promise<ProjectsSummary> {
  return apiRequest('/api/admin/dashboard/projects');
}

export async function getIntelligenceHealth(days = 7): Promise<IntelligenceHealth> {
  return apiRequest(`/api/admin/dashboard/intel-health?days=${days}`);
}

export async function getCostSummary(
  period: 'week' | 'month' | 'quarter' = 'month'
): Promise<CostSummary> {
  return apiRequest(`/api/admin/dashboard/costs?period=${period}`);
}

export async function getMasterCostSummary(
  period: 'week' | 'month' | 'quarter' = 'month',
  system?: string,
  company?: string,
  feature?: string
): Promise<MasterCostSummary> {
  const params = new URLSearchParams({ period });
  if (system) params.set('system', system);
  if (company) params.set('company', company);
  if (feature) params.set('feature', feature);
  return apiRequest(`/api/admin/dashboard/costs/master?${params}`);
}

export async function getCostBudgets(): Promise<CostBudget[]> {
  return apiRequest('/api/admin/dashboard/costs/budgets');
}

export async function createCostBudget(budget: {
  name: string;
  scope: string;
  scope_value?: string;
  period: string;
  budget_amount_usd: number;
  alert_threshold_pct?: number;
}): Promise<CostBudget> {
  return apiRequest('/api/admin/dashboard/costs/budgets', {
    method: 'POST',
    body: JSON.stringify(budget),
  });
}

export async function deleteCostBudget(budgetId: string): Promise<{ status: string }> {
  return apiRequest(`/api/admin/dashboard/costs/budgets/${budgetId}`, {
    method: 'DELETE',
  });
}

export async function acknowledgeCostAlert(alertId: string): Promise<{ status: string }> {
  return apiRequest(`/api/admin/dashboard/costs/alerts/${alertId}/acknowledge`, {
    method: 'POST',
  });
}

export async function getCostAlerts(acknowledged = false): Promise<CostAlert[]> {
  return apiRequest(`/api/admin/dashboard/costs/alerts?acknowledged=${acknowledged}`);
}

export interface CostHealthCheck {
  name: string;
  label: string;
  status: 'healthy' | 'warning' | 'error';
  last_activity: string | null;
  detail: string;
  metrics: Record<string, number>;
}

export interface CostTrackingHealth {
  overall_status: 'healthy' | 'warning' | 'error';
  healthy_count: number;
  total_checks: number;
  checks: CostHealthCheck[];
  checked_at: string;
}

export async function getCostTrackingHealth(): Promise<CostTrackingHealth> {
  return apiRequest('/api/admin/dashboard/costs/health');
}

export interface SystemCostDetail {
  system: string;
  total_cost: number;
  total_calls: number;
  input_tokens: number;
  output_tokens: number;
  by_model: { name: string; cost: number; calls: number; input_tokens?: number; output_tokens?: number }[];
  by_category: { name: string; cost: number; calls: number }[];
  by_provider: { name: string; cost: number; calls: number; input_tokens?: number; output_tokens?: number }[];
  by_feature: { name: string; cost: number; calls: number; input_tokens?: number; output_tokens?: number }[];
  by_user: { user_id: string; name: string; cost: number; calls: number }[];
  by_action_type?: { name: string; cost: number; count: number }[];
  by_status?: { name: string; count: number }[];
  daily_trend: { date: string; cost: number }[];
}

export async function getSystemCostDetail(
  system: string,
  period: 'week' | 'month' | 'quarter' = 'month'
): Promise<SystemCostDetail> {
  const params = new URLSearchParams({ period });
  return apiRequest(`/api/admin/dashboard/costs/system/${system}?${params}`);
}

// =============================================================================
// Team Status (Attendance Tracking)
// =============================================================================

export interface TeamMemberStatus {
  user_id: string;
  name: string;
  email: string;
  department: string;
  status: 'online' | 'away' | 'offline';
  session_start: string | null;
  last_activity: string | null;
  last_login: string | null;
  total_minutes_today: number;
  sessions_today: number;
  missed_clockouts_count: number;
  last_missed_clockout: string | null;
  has_pending_eod: boolean;
  // Activity tracking (v104)
  emails_sent: number;
  emails_received: number;
  slack_messages_sent: number;
  meetings_attended: number;
  meeting_minutes: number;
  break_minutes: number;
  break_count: number;
}

export async function getTeamStatus(): Promise<{ members: TeamMemberStatus[] }> {
  return apiRequest('/api/admin/dashboard/team-status');
}

export interface ClientStatus {
  user_id: string;
  email: string;
  display_name: string;
  client_name: string;
  // Engagement metrics
  engagement_score: number;
  user_tier: 'power_user' | 'active' | 'casual' | 'at_risk' | 'inactive';
  churn_risk_level: 'low' | 'medium' | 'high';
  // Activity stats
  last_login_at: string | null;
  days_since_last_login: number;
  active_days_last_7: number;
  active_days_last_30: number;
  // Lifetime stats
  lifetime_logins: number;
  lifetime_sessions: number;
  lifetime_minutes: number;
  features_adopted: number;
  // Weekly stats
  logins_this_week: number;
  sessions_this_week: number;
  minutes_this_week: number;
  // Today's activity
  command_center_visits_today: number;
  team_hub_visits_today: number;
  analytics_visits_today: number;
  searches_today: number;
  page_views_today: number;
}

export async function getClientStatus(): Promise<{ clients: ClientStatus[] }> {
  return apiRequest('/api/admin/dashboard/client-status');
}

// =============================================================================
// Attendance History API
// =============================================================================

export interface AttendanceHistoryRecord {
  period_start: string;
  days_worked: number;
  total_work_minutes: number;
  total_break_minutes: number;
  total_lunch_minutes: number;
  avg_daily_minutes: number;
  total_hours: number;
  events_count: number;
  user_id?: string;
  employee_name?: string;
  department?: string;
}

export interface AttendanceHistoryResponse {
  period: 'day' | 'week' | 'month';
  date_from: string;
  date_to: string;
  records: AttendanceHistoryRecord[];
  user_id?: string;
  user_name?: string;
}

export interface AttendanceDailyDetail {
  date: string;
  first_clock_in: string | null;
  last_clock_out: string | null;
  total_work_minutes: number;
  total_break_minutes: number;
  total_lunch_minutes: number;
  session_count: number;
  break_count: number;
  lunch_count: number;
  events: {
    id: string;
    event_type: string;
    event_timestamp: string;
    raw_message?: string;
    confidence: number;
  }[];
}

export interface AttendanceUserSummary {
  user_id: string;
  display_name: string;
  email: string;
  employee_name: string;
  department: string;
  week_start: string;
  days_worked: number;
  total_work_minutes: number;
  total_break_minutes: number;
  total_lunch_minutes: number;
  avg_daily_minutes: number;
  total_hours: number;
  total_breaks: number;
  total_lunches: number;
}

export async function getAttendanceHistory(params: {
  user_id?: string;
  date_from?: string;
  date_to?: string;
  period?: 'day' | 'week' | 'month';
}): Promise<AttendanceHistoryResponse> {
  const searchParams = new URLSearchParams();
  if (params.user_id) searchParams.append('user_id', params.user_id);
  if (params.date_from) searchParams.append('date_from', params.date_from);
  if (params.date_to) searchParams.append('date_to', params.date_to);
  if (params.period) searchParams.append('period', params.period);

  return apiRequest(`/api/admin/attendance/history?${searchParams.toString()}`);
}

export async function getAttendanceDaily(userId: string, date: string): Promise<AttendanceDailyDetail> {
  return apiRequest(`/api/admin/attendance/daily/${userId}?date=${date}`);
}

export async function getAttendanceWeeklySummary(params?: {
  week_start?: string;
}): Promise<{ summaries: AttendanceUserSummary[] }> {
  const searchParams = new URLSearchParams();
  if (params?.week_start) searchParams.append('week_start', params.week_start);

  return apiRequest(`/api/admin/attendance/weekly?${searchParams.toString()}`);
}

// =============================================================================
// Presence / Time Tracking API
// =============================================================================

export interface PresenceTeamMember {
  user_id: string;
  name: string;
  email?: string;
  department: string;
  clocked_in: boolean;
  current_status: string | null;
  status_since: string | null;
  minutes_worked: number;
  clock_in_at: string | null;
}

export interface WorkSession {
  id: string;
  user_id: string;
  clock_in_at: string;
  clock_out_at: string | null;
  total_minutes: number | null;
  date: string;
  eod_completed: boolean;
}

export interface AgendaItem {
  description: string;
  project_id?: string;
  project_name?: string;
  action_item_id?: string;
  priority: number;
}

export interface ClockInResponse {
  session_id: string;
  clock_in_at: string;
  message: string;
  agenda: AgendaItem[] | null;
}

export interface ClockOutResponse {
  success: boolean;
  session_id: string;
  total_minutes: number;
  message: string;
}

export interface PresenceStatusResponse {
  clocked_in: boolean;
  session: WorkSession | null;
  current_status: { status: string; started_at: string } | null;
  minutes_worked: number;
}

export interface TeamPresenceResponse {
  online_count: number;
  total_count: number;
  team: PresenceTeamMember[];
}

export interface EODContext {
  session_id: string;
  user_projects: Array<{ id: string; name: string; slug: string; project_type: string; status: string }>;
  user_tasks: Array<{ id: string; task: string; status: string; due_date: string; priority: string; project_id: string }>;
  today_chat_context: Array<{ id: string; title: string; created_at: string }>;
  team_members: Array<{ user_id: string; name: string; email: string; department: string; role: string }>;
}

export interface EODMessageResponse {
  assistant_message: string;
  extracted_data: any;
  suggested_matches: any[];
  next_step: string | null;
  is_complete: boolean;
}

export interface EODSubmitResponse {
  success: boolean;
  eod_id: string;
  slack_posted: boolean;
  message: string;
}

// Clock in for the day
export async function presenceClockIn(): Promise<ClockInResponse> {
  return apiRequest('/api/presence/clock-in', { method: 'POST' });
}

// Clock out (requires EOD completion)
export async function presenceClockOut(eodCompleted: boolean): Promise<ClockOutResponse> {
  return apiRequest('/api/presence/clock-out', {
    method: 'POST',
    body: JSON.stringify({ eod_completed: eodCompleted }),
  });
}

// Get current presence status
export async function getPresenceStatus(): Promise<PresenceStatusResponse> {
  return apiRequest('/api/presence/status');
}

// Update work status (available, lunch_break, short_break, in_meeting, away)
export async function updatePresenceStatus(status: string): Promise<{ success: boolean; status: string; started_at: string }> {
  return apiRequest('/api/presence/status', {
    method: 'POST',
    body: JSON.stringify({ status }),
  });
}

// Get team presence
export async function getTeamPresence(): Promise<TeamPresenceResponse> {
  return apiRequest('/api/presence/team');
}

// Start EOD flow
export async function startEOD(): Promise<EODContext> {
  return apiRequest('/api/presence/eod/start', { method: 'POST' });
}

// Process EOD message
export async function sendEODMessage(message: string, step: string): Promise<EODMessageResponse> {
  return apiRequest('/api/presence/eod/message', {
    method: 'POST',
    body: JSON.stringify({ message, step }),
  });
}

// Submit EOD
export async function submitEOD(
  completedItems: any[],
  blockers: any[],
  tomorrowPriorities: any[],
  rawConversation: any[]
): Promise<EODSubmitResponse> {
  return apiRequest('/api/presence/eod/submit', {
    method: 'POST',
    body: JSON.stringify({
      completed_items: completedItems,
      blockers,
      tomorrow_priorities: tomorrowPriorities,
      raw_conversation: rawConversation,
    }),
  });
}

// Enrich work description using AI
export async function enrichWorkDescription(
  originalInput: string,
  projectName: string,
  taskDetails: string
): Promise<{ enriched_description: string }> {
  return apiRequest('/api/presence/eod/enrich-work-description', {
    method: 'POST',
    body: JSON.stringify({
      original_input: originalInput,
      project_name: projectName,
      task_details: taskDetails,
    }),
  });
}

// Admin: Clock out another user
export async function adminClockOut(userId: string, reason: string): Promise<{ success: boolean; message: string }> {
  return apiRequest('/api/presence/admin/clock-out', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId, reason }),
  });
}

// Get active blockers (admin)
export async function getActiveBlockers(): Promise<{ blockers: any[]; total: number }> {
  return apiRequest('/api/presence/blockers');
}

// Update blocker status
export async function updateBlocker(
  blockerId: string,
  status: 'acknowledged' | 'resolved',
  resolutionNotes?: string
): Promise<{ success: boolean; blocker: any }> {
  const params = new URLSearchParams({ status });
  if (resolutionNotes) {
    params.append('resolution_notes', resolutionNotes);
  }
  return apiRequest(`/api/presence/blockers/${blockerId}?${params.toString()}`, {
    method: 'PATCH',
  });
}

// Get blockers assigned to current user (for Projects tab header)
export async function getMyBlockers(): Promise<{ blockers: Blocker[]; total: number }> {
  return apiRequest('/api/presence/blockers/my-blockers');
}

// Create blocker when moving project/task to Blocked column
export async function createBlockerFromMove(request: {
  entity_type: 'project' | 'task';
  entity_id: string;
  blocker_description: string;
  flagged_to_user_id: string;
  blocker_type?: string;
  create_task_for_resolver?: boolean;
}): Promise<{ success: boolean; blocker_id: string; resolver_task_id?: string }> {
  return apiRequest('/api/presence/blockers/from-move', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

// Resolve a blocker and unblock the associated entity
export async function resolveBlockerWithUnblock(
  blockerId: string,
  resolutionNotes?: string,
  unblockEntity: boolean = true
): Promise<{ success: boolean }> {
  return apiRequest(`/api/presence/blockers/${blockerId}/resolve`, {
    method: 'POST',
    body: JSON.stringify({
      resolution_notes: resolutionNotes,
      unblock_entity: unblockEntity,
    }),
  });
}

// Acknowledge a blocker (mark as being worked on)
export async function acknowledgeBlocker(blockerId: string): Promise<{ success: boolean; blocker: any }> {
  return updateBlocker(blockerId, 'acknowledged');
}

// =============================================================================
// Projects API
// =============================================================================

import type {
  Project,
  ProjectSearchRequest,
  ProjectSearchResponse,
  ProjectCreateRequest,
  ProjectUpdateRequest,
  ProjectStats,
  Task,
  TaskSearchRequest,
  TaskSearchResponse,
  TaskCreateRequest,
  TaskUpdateRequest,
  BulkTaskUpdateRequest,
  MyWorkResponse,
  MyDayResponse,
  Attachment,
  Comment,
  CommentCreateRequest,
  Company,
  TaskStatus,
  Blocker,
} from '@/types/projects';

// Re-export types for convenience
export type {
  Project,
  ProjectSearchRequest,
  ProjectSearchResponse,
  ProjectCreateRequest,
  ProjectUpdateRequest,
  ProjectStats,
  Task,
  TaskSearchRequest,
  TaskSearchResponse,
  TaskCreateRequest,
  TaskUpdateRequest,
  BulkTaskUpdateRequest,
  MyWorkResponse,
  MyDayResponse,
  Attachment,
  Comment,
  CommentCreateRequest,
};

// Projects
export async function searchProjects(
  request: ProjectSearchRequest
): Promise<ProjectSearchResponse> {
  return apiRequest('/api/projects/search', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export async function getProject(projectId: string): Promise<Project> {
  return apiRequest(`/api/projects/${projectId}`);
}

export async function createProject(
  request: ProjectCreateRequest
): Promise<Project> {
  return apiRequest('/api/projects', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export async function updateProject(
  projectId: string,
  request: ProjectUpdateRequest
): Promise<Project> {
  return apiRequest(`/api/projects/${projectId}`, {
    method: 'PATCH',
    body: JSON.stringify(request),
  });
}

export async function getProjectStats(company?: Company): Promise<ProjectStats> {
  const params = company ? `?company=${company}` : '';
  return apiRequest(`/api/projects/stats${params}`);
}

export async function getProjectTasks(
  projectId: string,
  status?: TaskStatus[],
  includeSubtasks: boolean = true
): Promise<Task[]> {
  const params = new URLSearchParams();
  if (status && status.length > 0) {
    status.forEach(s => params.append('status', s));
  }
  params.append('include_subtasks', String(includeSubtasks));
  return apiRequest(`/api/projects/${projectId}/tasks?${params.toString()}`);
}

export async function getMyWork(
  status?: TaskStatus[],
  limit: number = 50,
  offset: number = 0
): Promise<MyWorkResponse> {
  const params = new URLSearchParams();
  if (status && status.length > 0) {
    status.forEach(s => params.append('status', s));
  }
  params.append('limit', String(limit));
  params.append('offset', String(offset));
  return apiRequest(`/api/projects/my-work?${params.toString()}`);
}

export async function getMyDay(): Promise<MyDayResponse> {
  return apiRequest('/api/projects/my-day');
}

// Tasks
export async function searchTasks(
  request: TaskSearchRequest
): Promise<TaskSearchResponse> {
  return apiRequest('/api/tasks/search', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export async function getTask(
  taskId: string,
  includeSubtasks: boolean = true
): Promise<Task> {
  return apiRequest(`/api/tasks/${taskId}?include_subtasks=${includeSubtasks}`);
}

export async function createTask(request: TaskCreateRequest): Promise<Task> {
  return apiRequest('/api/tasks', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export async function updateTask(
  taskId: string,
  request: TaskUpdateRequest
): Promise<Task> {
  return apiRequest(`/api/tasks/${taskId}`, {
    method: 'PATCH',
    body: JSON.stringify(request),
  });
}

export async function deleteTask(taskId: string): Promise<{ message: string; id: string }> {
  return apiRequest(`/api/tasks/${taskId}`, { method: 'DELETE' });
}

export async function bulkUpdateTasks(
  request: BulkTaskUpdateRequest
): Promise<{ message: string; updated_count: number; task_ids: string[] }> {
  return apiRequest('/api/tasks/bulk-update', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

// Attachments
export async function getTaskAttachments(taskId: string): Promise<Attachment[]> {
  return apiRequest(`/api/tasks/${taskId}/attachments`);
}

export async function uploadTaskAttachment(
  taskId: string,
  file: File,
  fileType: 'image' | 'voice_note' | 'document',
  durationSeconds?: number
): Promise<Attachment> {
  const formData = new FormData();
  formData.append('file', file);

  const params = new URLSearchParams();
  params.append('file_type', fileType);
  if (durationSeconds !== undefined) {
    params.append('duration_seconds', String(durationSeconds));
  }

  const token = await getAccessToken();
  const response = await fetch(
    `${API_BASE}/api/tasks/${taskId}/attachments?${params.toString()}`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
    throw new Error(error.detail || 'Upload failed');
  }

  return response.json();
}

export async function deleteTaskAttachment(
  taskId: string,
  attachmentId: string
): Promise<{ message: string; id: string }> {
  return apiRequest(`/api/tasks/${taskId}/attachments/${attachmentId}`, {
    method: 'DELETE',
  });
}

// Comments
export async function getTaskComments(taskId: string): Promise<Comment[]> {
  return apiRequest(`/api/tasks/${taskId}/comments`);
}

export async function createTaskComment(
  taskId: string,
  request: CommentCreateRequest
): Promise<Comment> {
  return apiRequest(`/api/tasks/${taskId}/comments`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export async function deleteTaskComment(
  taskId: string,
  commentId: string
): Promise<{ message: string; id: string }> {
  return apiRequest(`/api/tasks/${taskId}/comments/${commentId}`, {
    method: 'DELETE',
  });
}

// =============================================================================
// Notifications API
// =============================================================================

import type {
  Notification,
  NotificationsListResponse,
  UnreadCountResponse,
  MarkReadResponse,
  MarkAllReadResponse,
  ClearAllResponse,
  ArchiveResponse,
  NotificationPreferences,
  NotificationType,
} from '@/types/notifications';

// Re-export types for convenience
export type {
  Notification,
  NotificationsListResponse,
  UnreadCountResponse,
  NotificationPreferences,
  NotificationType,
};

/**
 * Get notifications for the current user
 */
export async function getNotifications(
  page: number = 1,
  pageSize: number = 20,
  unreadOnly: boolean = false,
  types?: NotificationType[],
  priority?: string,
  isPersonal?: boolean,
  includeChat?: boolean,  // Phase 2.3
  chatOnly?: boolean,  // Phase 2.3
  search?: string  // Phase 3.2
): Promise<NotificationsListResponse> {
  const params = new URLSearchParams();
  params.append('page', String(page));
  params.append('page_size', String(pageSize));
  if (unreadOnly) params.append('unread_only', 'true');
  if (types && types.length > 0) {
    types.forEach(t => params.append('types', t));
  }
  if (priority) params.append('priority', priority);
  if (isPersonal !== undefined) params.append('is_personal', String(isPersonal));
  if (includeChat !== undefined) params.append('include_chat', String(includeChat));  // Phase 2.3
  if (chatOnly) params.append('chat_only', 'true');  // Phase 2.3
  if (search) params.append('search', search);  // Phase 3.2
  return apiRequest(`/api/notifications?${params.toString()}`);
}

/**
 * Get unread notification count (lightweight endpoint for polling)
 */
export async function getUnreadNotificationCount(): Promise<UnreadCountResponse> {
  return apiRequest('/api/notifications/unread-count');
}

/**
 * Mark a single notification as read
 */
export async function markNotificationRead(notificationId: string): Promise<MarkReadResponse> {
  return apiRequest(`/api/notifications/${notificationId}/read`, {
    method: 'PATCH',
  });
}

/**
 * Mark all notifications as read
 */
export async function markAllNotificationsRead(): Promise<MarkAllReadResponse> {
  return apiRequest('/api/notifications/mark-all-read', {
    method: 'POST',
  });
}

/**
 * Clear all notifications (mark as read + archive)
 * Most aggressive cleanup operation
 */
export async function clearAllNotifications(): Promise<ClearAllResponse> {
  return apiRequest('/api/notifications/clear-all', {
    method: 'POST',
  });
}

/**
 * Archive (soft delete) a notification
 */
export async function archiveNotification(notificationId: string): Promise<ArchiveResponse> {
  return apiRequest(`/api/notifications/${notificationId}`, {
    method: 'DELETE',
  });
}

/**
 * Get notification preferences for the current user
 */
export async function getNotificationPreferences(): Promise<NotificationPreferences> {
  return apiRequest('/api/notifications/preferences');
}

/**
 * Update notification preferences
 */
export async function updateNotificationPreferences(
  updates: Partial<Pick<NotificationPreferences, 'disabled_types' | 'sound_enabled' | 'show_previews'>>
): Promise<NotificationPreferences> {
  // Fetch current preferences first to merge, avoiding overwriting unset fields with defaults
  const current = await getNotificationPreferences();
  const merged = { ...current, ...updates };
  return apiRequest('/api/notifications/preferences', {
    method: 'PUT',
    body: JSON.stringify(merged),
  });
}

// =============================================================================
// QA Workflow API
// =============================================================================

import type {
  QATemplate,
  QASummary,
  QATrail,
  MyReviewTask,
  QAIncident,
  InitializeTemplateRequest,
  InitializeCustomRequest,
  InitializeSimpleRequest,
  ApproveStageRequest,
  RejectStageRequest,
  SkipStageRequest,
  AddStageRequest,
  FileVersion,
  VersionList,
} from '@/types/qa';

// Re-export QA types
export type {
  QATemplate,
  QASummary,
  QATrail,
  MyReviewTask,
  QAIncident,
  FileVersion,
  VersionList,
};

/**
 * Get list of QA templates
 */
export async function getQATemplates(): Promise<QATemplate[]> {
  return apiRequest('/api/qa/templates');
}

/**
 * Initialize QA from a template
 */
export async function initializeQAFromTemplate(
  taskId: string,
  request: InitializeTemplateRequest
): Promise<{ success: boolean; template_name: string; total_stages: number; first_stage_id: string }> {
  return apiRequest(`/api/qa/tasks/${taskId}/initialize/template`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Initialize QA with custom stages
 */
export async function initializeQACustom(
  taskId: string,
  request: InitializeCustomRequest
): Promise<{ success: boolean; total_stages: number; first_stage_id: string }> {
  return apiRequest(`/api/qa/tasks/${taskId}/initialize/custom`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Initialize simple approval (one person)
 */
export async function initializeSimpleApproval(
  taskId: string,
  request: InitializeSimpleRequest
): Promise<{ success: boolean; stage_id: string; reviewer_name: string }> {
  return apiRequest(`/api/qa/tasks/${taskId}/initialize/simple`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Get QA summary for a task
 */
export async function getQASummary(taskId: string): Promise<QASummary> {
  return apiRequest(`/api/qa/tasks/${taskId}/summary`);
}

/**
 * Approve the current QA stage
 */
export async function approveQAStage(
  taskId: string,
  request: ApproveStageRequest
): Promise<{ success: boolean; approved_stage: string; is_complete: boolean; next_stage_id?: string; next_stage_name?: string }> {
  return apiRequest(`/api/qa/tasks/${taskId}/approve`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Reject the current QA stage
 */
export async function rejectQAStage(
  taskId: string,
  request: RejectStageRequest
): Promise<{ success: boolean; rejected_stage: string; incident_id: string; goes_back_to: string }> {
  return apiRequest(`/api/qa/tasks/${taskId}/reject`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Skip a QA stage
 */
export async function skipQAStage(
  taskId: string,
  request: SkipStageRequest
): Promise<{ success: boolean; skipped_stage: string; next_stage_id?: string; next_stage_name?: string }> {
  return apiRequest(`/api/qa/tasks/${taskId}/skip`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Add a stage mid-workflow
 */
export async function addQAStage(
  taskId: string,
  request: AddStageRequest
): Promise<{ success: boolean; new_stage_id: string; stage_order: number }> {
  return apiRequest(`/api/qa/tasks/${taskId}/stages`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Remove a pending stage
 */
export async function removeQAStage(
  taskId: string,
  stageId: string,
  reason: string
): Promise<{ success: boolean; removed_stage: string }> {
  return apiRequest(`/api/qa/tasks/${taskId}/stages/${stageId}?reason=${encodeURIComponent(reason)}`, {
    method: 'DELETE',
  });
}

/**
 * Get full QA trail for a task
 */
export async function getQATrail(taskId: string): Promise<QATrail> {
  return apiRequest(`/api/qa/tasks/${taskId}/trail`);
}

/**
 * Get tasks pending my review
 */
export async function getMyReviews(): Promise<MyReviewTask[]> {
  return apiRequest('/api/qa/my-reviews');
}

/**
 * List QA incidents
 */
export async function getQAIncidents(params?: {
  employee_id?: string;
  department?: string;
  is_resolved?: boolean;
  limit?: number;
  offset?: number;
}): Promise<QAIncident[]> {
  const queryParams = new URLSearchParams();
  if (params?.employee_id) queryParams.append('employee_id', params.employee_id);
  if (params?.department) queryParams.append('department', params.department);
  if (params?.is_resolved !== undefined) queryParams.append('is_resolved', String(params.is_resolved));
  if (params?.limit) queryParams.append('limit', String(params.limit));
  if (params?.offset) queryParams.append('offset', String(params.offset));

  const query = queryParams.toString();
  return apiRequest(`/api/qa/incidents${query ? '?' + query : ''}`);
}

/**
 * Resolve a QA incident
 */
export async function resolveQAIncident(
  incidentId: string,
  resolutionNotes: string
): Promise<{ success: boolean; message: string }> {
  return apiRequest(`/api/qa/incidents/${incidentId}/resolve?resolution_notes=${encodeURIComponent(resolutionNotes)}`, {
    method: 'PATCH',
  });
}

// =============================================================================
// Asset-Based Versioning API (v52/v53 - replaces legacy File Versions)
// =============================================================================

import type {
  Asset,
  AssetList,
  AssetDetail,
  AssetVersion,
  DeletedAsset,
  AssetAuditEntry,
  AssetStatusItem,
  CanAdvanceResult,
  UpdateAssetRequest,
  UpdateVersionRequest,
  ApproveAssetRequest,
  RejectAssetRequest,
} from '@/types/qa';

/**
 * List all assets for a task
 */
export async function getTaskAssets(taskId: string, includeDeleted = false): Promise<AssetList> {
  const params = includeDeleted ? '?include_deleted=true' : '';
  return apiRequest(`/api/tasks/${taskId}/assets${params}`);
}

/**
 * Create a new asset with file upload
 */
export async function createAsset(
  taskId: string,
  file: File,
  assetName: string,
  options?: {
    description?: string;
    notes?: string;
    autoApprove?: boolean;
  }
): Promise<{ success: boolean; asset_id: string; version_id: string; version_number: number }> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('asset_name', assetName);
  if (options?.description) formData.append('description', options.description);
  if (options?.notes) formData.append('notes', options.notes);
  formData.append('auto_approve', String(options?.autoApprove ?? true));

  const token = await getAccessToken();
  const response = await fetch(
    `${API_BASE}/api/tasks/${taskId}/assets`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
    throw new Error(error.detail || 'Upload failed');
  }

  return response.json();
}

/**
 * Create a new link-type asset (external URL)
 */
export async function createLinkAsset(
  taskId: string,
  assetName: string,
  externalUrl: string,
  options?: {
    description?: string;
    notes?: string;
    autoApprove?: boolean;
  }
): Promise<{ success: boolean; asset_id: string; version_id: string; version_number: number }> {
  const formData = new FormData();
  formData.append('asset_name', assetName);
  formData.append('external_url', externalUrl);
  if (options?.description) formData.append('description', options.description);
  if (options?.notes) formData.append('notes', options.notes);
  formData.append('auto_approve', String(options?.autoApprove ?? true));

  const token = await getAccessToken();
  const response = await fetch(
    `${API_BASE}/api/tasks/${taskId}/assets`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to create link asset' }));
    throw new Error(error.detail || 'Failed to create link asset');
  }

  return response.json();
}

/**
 * Get asset detail with all versions
 */
export async function getAssetDetail(
  taskId: string,
  assetId: string,
  includeDeletedVersions = false
): Promise<AssetDetail> {
  const params = includeDeletedVersions ? '?include_deleted_versions=true' : '';
  return apiRequest(`/api/tasks/${taskId}/assets/${assetId}${params}`);
}

/**
 * Update asset metadata
 */
export async function updateAsset(
  taskId: string,
  assetId: string,
  data: UpdateAssetRequest
): Promise<{ success: boolean; message: string }> {
  return apiRequest(`/api/tasks/${taskId}/assets/${assetId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Soft-delete an asset
 */
export async function deleteAsset(
  taskId: string,
  assetId: string,
  reason?: string
): Promise<{ success: boolean; asset_id: string; deleted_by: string; deleted_at: string }> {
  const params = reason ? `?reason=${encodeURIComponent(reason)}` : '';
  return apiRequest(`/api/tasks/${taskId}/assets/${assetId}${params}`, {
    method: 'DELETE',
  });
}

/**
 * Upload a new version for an existing asset
 */
export async function uploadAssetVersion(
  taskId: string,
  assetId: string,
  file: File,
  options?: {
    notes?: string;
    autoApprove?: boolean;
  }
): Promise<{ success: boolean; asset_id: string; version_id: string; version_number: number }> {
  const formData = new FormData();
  formData.append('file', file);
  if (options?.notes) formData.append('notes', options.notes);
  formData.append('auto_approve', String(options?.autoApprove ?? true));

  const token = await getAccessToken();
  const response = await fetch(
    `${API_BASE}/api/tasks/${taskId}/assets/${assetId}/versions`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
    throw new Error(error.detail || 'Upload failed');
  }

  return response.json();
}

/**
 * Upload a new link version for an existing asset
 */
export async function uploadAssetLinkVersion(
  taskId: string,
  assetId: string,
  externalUrl: string,
  options?: {
    notes?: string;
    autoApprove?: boolean;
  }
): Promise<{ success: boolean; asset_id: string; version_id: string; version_number: number }> {
  const formData = new FormData();
  formData.append('external_url', externalUrl);
  if (options?.notes) formData.append('notes', options.notes);
  formData.append('auto_approve', String(options?.autoApprove ?? true));

  const token = await getAccessToken();
  const response = await fetch(
    `${API_BASE}/api/tasks/${taskId}/assets/${assetId}/versions`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to create link version' }));
    throw new Error(error.detail || 'Failed to create link version');
  }

  return response.json();
}

/**
 * Get a specific version
 */
export async function getAssetVersion(
  taskId: string,
  assetId: string,
  versionId: string
): Promise<AssetVersion> {
  return apiRequest(`/api/tasks/${taskId}/assets/${assetId}/versions/${versionId}`);
}

/**
 * Update version notes
 */
export async function updateAssetVersion(
  taskId: string,
  assetId: string,
  versionId: string,
  data: UpdateVersionRequest
): Promise<{ success: boolean; message: string }> {
  return apiRequest(`/api/tasks/${taskId}/assets/${assetId}/versions/${versionId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Soft-delete a version
 */
export async function deleteAssetVersion(
  taskId: string,
  assetId: string,
  versionId: string
): Promise<{ success: boolean; version_id: string; deleted_by: string }> {
  return apiRequest(`/api/tasks/${taskId}/assets/${assetId}/versions/${versionId}`, {
    method: 'DELETE',
  });
}

/**
 * List deleted assets for restore UI
 */
export async function getDeletedAssets(taskId: string): Promise<{ task_id: string; deleted_assets: DeletedAsset[] }> {
  return apiRequest(`/api/tasks/${taskId}/assets/deleted`);
}

/**
 * Restore a soft-deleted asset
 */
export async function restoreAsset(
  taskId: string,
  assetId: string
): Promise<{ success: boolean; asset_id: string; restored_by: string; restored_at: string }> {
  return apiRequest(`/api/tasks/${taskId}/assets/${assetId}/restore`, {
    method: 'POST',
  });
}

/**
 * Get audit trail for assets in a task
 */
export async function getAssetAuditTrail(
  taskId: string,
  assetId?: string,
  limit = 50
): Promise<{ task_id: string; asset_id?: string; audit_entries: AssetAuditEntry[] }> {
  const params = new URLSearchParams();
  if (assetId) params.append('asset_id', assetId);
  params.append('limit', String(limit));
  return apiRequest(`/api/tasks/${taskId}/assets/audit?${params.toString()}`);
}

// =============================================================================
// Per-Asset QA Approval API
// =============================================================================

/**
 * Get approval status for all assets in a task at current QA stage
 */
export async function getTaskAssetStatus(taskId: string): Promise<{ task_id: string; assets: AssetStatusItem[] }> {
  return apiRequest(`/api/qa/tasks/${taskId}/asset-status`);
}

/**
 * Check if all assets are approved and task can advance to next stage
 */
export async function checkCanAdvanceStage(taskId: string): Promise<CanAdvanceResult> {
  return apiRequest(`/api/qa/tasks/${taskId}/can-advance`);
}

/**
 * Approve an asset at the current QA stage
 */
export async function approveAsset(
  assetId: string,
  data?: ApproveAssetRequest
): Promise<{ success: boolean; asset_id: string; stage_name: string; approved_by: string }> {
  return apiRequest(`/api/qa/assets/${assetId}/approve`, {
    method: 'POST',
    body: JSON.stringify(data || {}),
  });
}

/**
 * Reject an asset at the current QA stage
 */
export async function rejectAsset(
  assetId: string,
  data: RejectAssetRequest
): Promise<{ success: boolean; asset_id: string; stage_name: string; rejected_by: string; original_uploader_id: string; original_uploader_name: string }> {
  return apiRequest(`/api/qa/assets/${assetId}/reject`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Get approval history for an asset across all QA stages
 */
export async function getAssetApprovalHistory(assetId: string): Promise<{ asset_id: string; approvals: import('@/types/qa').AssetStageApproval[] }> {
  return apiRequest(`/api/qa/assets/${assetId}/history`);
}

// =============================================================================
// QA WORKFLOW V2 API (Branching Workflow with AI QA)
// =============================================================================

import type {
  WorkflowV2Summary,
  WorkflowStatusResponse,
  AssetProgress,
  BriefAnalysis,
  BriefCompletionState,
  QAAuditLogEntry,
  AuditTrailResponse,
  TeamMember,
  TeamForRoleResponse,
  InitializeV2Request,
  BriefAnalyzeRequest,
  BriefCompleteRequest,
  AssignPersonRequest,
  AIKickbackRequest,
  AIIssueSeverity,
  CompleteStageRequest,
  CompleteStageResponse,
} from '@/types/qa';

/**
 * Initialize V2 branching workflow for a task
 */
export async function initializeWorkflowV2(
  taskId: string,
  request?: InitializeV2Request
): Promise<{ success: boolean; message: string; workflow: WorkflowV2Summary }> {
  return apiRequest(`/api/qa/tasks/${taskId}/initialize/v2`, {
    method: 'POST',
    body: JSON.stringify(request || {}),
  });
}

/**
 * Get full workflow status including parallel groups and current position
 */
export async function getWorkflowStatus(taskId: string): Promise<WorkflowStatusResponse> {
  return apiRequest(`/api/qa/tasks/${taskId}/workflow-status`);
}

/**
 * Get asset progress for a specific stage (pie chart data)
 */
export async function getAssetProgress(
  taskId: string,
  stageId: string
): Promise<AssetProgress> {
  return apiRequest(`/api/qa/tasks/${taskId}/asset-progress/${stageId}`);
}

/**
 * Trigger AI brief analysis
 */
export async function analyzeBrief(
  taskId: string,
  request: BriefAnalyzeRequest
): Promise<BriefAnalysis> {
  return apiRequest(`/api/qa/tasks/${taskId}/brief/analyze`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Mark brief as complete (triggers AI Brief QA node)
 */
export async function completeBrief(
  taskId: string,
  request?: BriefCompleteRequest
): Promise<{ success: boolean; message: string; analysis?: BriefAnalysis; advanced_to?: string }> {
  return apiRequest(`/api/qa/tasks/${taskId}/brief/complete`, {
    method: 'POST',
    body: JSON.stringify(request || {}),
  });
}

/**
 * Complete a stage and optionally trigger AI QA
 * For Copywriter: triggers AI Brief QA
 * For Graphic Designer: triggers AI Visual QA
 * For Final QA/CSM: advances to next stage
 */
export async function completeStage(
  taskId: string,
  stageId: string,
  request?: CompleteStageRequest
): Promise<CompleteStageResponse> {
  return apiRequest(`/api/qa/tasks/${taskId}/stages/${stageId}/complete`, {
    method: 'POST',
    body: JSON.stringify(request || {}),
  });
}

/**
 * Process AI kickback decision
 */
export async function processAIKickback(
  taskId: string,
  request: AIKickbackRequest
): Promise<{ success: boolean; message: string; assets_reverted: number; target_stage: string }> {
  return apiRequest(`/api/qa/tasks/${taskId}/ai-kickback`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Assign a person to a stage
 */
export async function assignPersonToStage(
  stageId: string,
  request: AssignPersonRequest
): Promise<{ success: boolean; message: string; stage_id: string; assigned_to: string }> {
  return apiRequest(`/api/qa/stages/${stageId}/assign`, {
    method: 'PATCH',
    body: JSON.stringify(request),
  });
}

/**
 * Get full audit trail for a task
 */
export async function getQAAuditTrail(
  taskId: string,
  params?: {
    limit?: number;
    offset?: number;
    action_type?: string;
    actor_type?: string;
  }
): Promise<AuditTrailResponse> {
  const queryParams = new URLSearchParams();
  if (params?.limit) queryParams.append('limit', String(params.limit));
  if (params?.offset) queryParams.append('offset', String(params.offset));
  if (params?.action_type) queryParams.append('action_type', params.action_type);
  if (params?.actor_type) queryParams.append('actor_type', params.actor_type);

  const query = queryParams.toString();
  return apiRequest(`/api/qa/tasks/${taskId}/audit-trail${query ? '?' + query : ''}`);
}

/**
 * Get team members available for a specific role
 */
export async function getTeamForRole(
  taskId: string,
  role: string
): Promise<TeamForRoleResponse> {
  return apiRequest(`/api/qa/tasks/${taskId}/team-for-role/${encodeURIComponent(role)}`);
}

// =============================================================================
// QA V2 ENHANCEMENTS - SLA, Templates, Notifications, Dashboard (sm_v112)
// =============================================================================

import type {
  SLAConfig,
  SLAStatus,
  RejectionTemplate,
  RejectWithTemplateRequest,
  PendingReview,
  TeamBacklogItem,
  AssetVersion,
} from '@/types/qa';

/**
 * Get rejection templates (optionally filtered)
 */
export async function getRejectionTemplates(
  category?: string,
  stage?: string
): Promise<RejectionTemplate[]> {
  const params = new URLSearchParams();
  if (category) params.append('category', category);
  if (stage) params.append('stage', stage);
  const query = params.toString();
  return apiRequest(`/api/qa/rejection-templates${query ? `?${query}` : ''}`);
}

/**
 * Record usage of a rejection template
 */
export async function useRejectionTemplate(templateId: string): Promise<void> {
  await apiRequest(`/api/qa/rejection-templates/${templateId}/use`, {
    method: 'POST',
  });
}

/**
 * Get SLA configuration for all stages
 */
export async function getSLAConfig(): Promise<SLAConfig[]> {
  return apiRequest('/api/qa/sla-config');
}

/**
 * Update SLA configuration
 */
export async function updateSLAConfig(
  configId: string,
  slaHours: number,
  warningThreshold: number
): Promise<SLAConfig> {
  return apiRequest(
    `/api/qa/sla-config/${configId}?sla_hours=${slaHours}&warning_threshold=${warningThreshold}`,
    { method: 'PATCH' }
  );
}

/**
 * Get SLA status for a specific stage
 */
export async function getStageSLAStatus(
  taskId: string,
  stageId: string
): Promise<SLAStatus> {
  return apiRequest(`/api/qa/tasks/${taskId}/stages/${stageId}/sla`);
}

/**
 * Get my pending reviews (dashboard widget)
 */
export async function getMyPendingReviews(): Promise<PendingReview[]> {
  return apiRequest('/api/qa/dashboard/my-pending-reviews');
}

/**
 * Get team QA backlog (dashboard widget)
 */
export async function getTeamBacklog(): Promise<TeamBacklogItem[]> {
  return apiRequest('/api/qa/dashboard/team-backlog');
}

/**
 * Get asset version history
 */
export async function getAssetVersionHistory(
  taskId: string,
  assetId: string
): Promise<AssetVersion[]> {
  return apiRequest(`/api/qa/tasks/${taskId}/assets/${assetId}/versions`);
}

/**
 * Reject asset with template (includes @mention support)
 */
export async function rejectAssetWithTemplate(
  taskId: string,
  assetId: string,
  stageId: string,
  request: RejectWithTemplateRequest
): Promise<{ success: boolean; reason: string }> {
  return apiRequest(
    `/api/qa/tasks/${taskId}/assets/${assetId}/reject-with-template?stage_id=${stageId}`,
    {
      method: 'POST',
      body: JSON.stringify(request),
    }
  );
}

/**
 * Send a QA notification
 */
export async function sendQANotification(
  taskId: string,
  notificationType: string,
  message: string,
  recipientId?: string,
  recipientSlackId?: string,
  stageId?: string,
  assetId?: string
): Promise<{ success: boolean; notification_id: string }> {
  const params = new URLSearchParams();
  if (stageId) params.append('stage_id', stageId);
  if (assetId) params.append('asset_id', assetId);
  const query = params.toString();

  return apiRequest(`/api/qa/tasks/${taskId}/notify${query ? `?${query}` : ''}`, {
    method: 'POST',
    body: JSON.stringify({
      notification_type: notificationType,
      message,
      recipient_id: recipientId,
      recipient_slack_id: recipientSlackId,
    }),
  });
}

// =============================================================================
// LEGACY File Versions API (DEPRECATED - use Asset API above)
// =============================================================================

/**
 * @deprecated Use getTaskAssets instead
 */
export async function getTaskVersions(taskId: string): Promise<VersionList> {
  console.warn('getTaskVersions is deprecated. Use getTaskAssets instead.');
  return apiRequest(`/api/tasks/${taskId}/versions`);
}

/**
 * @deprecated Use createAsset instead
 */
export async function createTaskVersion(
  taskId: string,
  files: File[],
  notes?: string,
  qaStageId?: string
): Promise<FileVersion> {
  console.warn('createTaskVersion is deprecated. Use createAsset instead.');
  const formData = new FormData();
  files.forEach(file => formData.append('files', file));
  if (notes) formData.append('notes', notes);
  if (qaStageId) formData.append('qa_stage_id', qaStageId);

  const token = await getAccessToken();
  const response = await fetch(
    `${API_BASE}/api/tasks/${taskId}/versions`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
    throw new Error(error.detail || 'Upload failed');
  }

  return response.json();
}

/**
 * @deprecated Use getAssetVersion instead
 */
export async function getTaskVersion(taskId: string, versionId: string): Promise<FileVersion> {
  console.warn('getTaskVersion is deprecated. Use getAssetVersion instead.');
  return apiRequest(`/api/tasks/${taskId}/versions/${versionId}`);
}

/**
 * @deprecated Use uploadAssetVersion instead
 */
export async function addFileToVersion(
  taskId: string,
  versionId: string,
  file: File
): Promise<import('@/types/qa').VersionFile> {
  console.warn('addFileToVersion is deprecated. Use uploadAssetVersion instead.');
  const formData = new FormData();
  formData.append('file', file);

  const token = await getAccessToken();
  const response = await fetch(
    `${API_BASE}/api/tasks/${taskId}/versions/${versionId}/files`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
    throw new Error(error.detail || 'Upload failed');
  }

  return response.json();
}

/**
 * @deprecated Use deleteAssetVersion instead
 */
export async function deleteTaskVersion(
  taskId: string,
  versionId: string
): Promise<{ success: boolean; message: string }> {
  console.warn('deleteTaskVersion is deprecated. Use deleteAssetVersion instead.');
  return apiRequest(`/api/tasks/${taskId}/versions/${versionId}`, {
    method: 'DELETE',
  });
}

/**
 * @deprecated Assets have single files per version, use deleteAssetVersion instead
 */
export async function deleteVersionFile(
  taskId: string,
  versionId: string,
  fileId: string
): Promise<{ success: boolean; message: string }> {
  console.warn('deleteVersionFile is deprecated. Use deleteAssetVersion instead.');
  return apiRequest(`/api/tasks/${taskId}/versions/${versionId}/files/${fileId}`, {
    method: 'DELETE',
  });
}

// =============================================================================
// News Intelligence API
// =============================================================================

import type {
  DailyNewsResponse,
  AvailableDatesResponse,
  ProductDetailResponse,
  StarRequest,
  UnstarRequest,
  BulkStarRequest,
  ReorderRequest,
  StarredContentResponse,
  StarResponse,
} from '@/types/news';

/**
 * Get news and products for a specific date
 */
export async function getDailyNews(date: string): Promise<DailyNewsResponse> {
  return apiRequest(`/api/news/daily/${date}`);
}

/**
 * Get available dates with news/product counts
 */
export async function getAvailableDates(days: number = 7): Promise<AvailableDatesResponse> {
  return apiRequest(`/api/news/dates?days=${days}`);
}

/**
 * Get dates around a specific date (for lazy loading older dates)
 */
export async function getDateRange(centerDate: string, buffer: number = 2): Promise<AvailableDatesResponse> {
  return apiRequest(`/api/news/dates/range?center_date=${centerDate}&buffer=${buffer}`);
}

/**
 * Get full product detail including enrichment
 */
export async function getProductDetail(productId: string): Promise<ProductDetailResponse> {
  return apiRequest(`/api/news/products/${productId}`);
}

/**
 * Trigger enrichment for a product
 */
export async function enrichProduct(productId: string): Promise<ProductDetailResponse> {
  return apiRequest(`/api/news/products/${productId}/enrich`, {
    method: 'POST',
  });
}

// =============================================================================
// Starred Content API (Manual content curation for reports)
// =============================================================================

/**
 * Star a news article or product for monthly reports
 */
export async function starContent(request: StarRequest): Promise<StarResponse> {
  return apiRequest('/api/starred-content/star', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Unstar a news article or product
 */
export async function unstarContent(request: UnstarRequest): Promise<{ success: boolean; message: string }> {
  return apiRequest('/api/starred-content/unstar', {
    method: 'DELETE',
    body: JSON.stringify(request),
  });
}

/**
 * Get all starred content for a specific month
 */
export async function getStarredContent(reportMonth: string): Promise<StarredContentResponse> {
  return apiRequest(`/api/starred-content/${reportMonth}`);
}

/**
 * Reorder starred content (after drag-and-drop)
 */
export async function reorderStarredContent(request: ReorderRequest): Promise<{ success: boolean; message: string }> {
  return apiRequest('/api/starred-content/reorder', {
    method: 'PUT',
    body: JSON.stringify(request),
  });
}

/**
 * Bulk star multiple items at once
 */
export async function bulkStarContent(request: BulkStarRequest): Promise<{ success: boolean; count: number; message: string }> {
  return apiRequest('/api/starred-content/bulk-star', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

// =============================================================================
// Client Portal API
// =============================================================================

import type {
  PortalLoginRequest,
  PortalLoginResponse,
  PortalUser,
  PortalClientSummary,
  PortalDashboard,
  PendingDeliverable,
  ClientRequest,
  CreateRequestForm,
  DeliverableApprovalRequest,
  WeeklyReport,
} from '@/types/portal';

// Portal session token storage key
const PORTAL_TOKEN_KEY = 'portal_session_token';

/**
 * Get portal session token from storage
 */
export function getPortalToken(): string | null {
  return localStorage.getItem(PORTAL_TOKEN_KEY);
}

/**
 * Set portal session token in storage
 */
export function setPortalToken(token: string): void {
  localStorage.setItem(PORTAL_TOKEN_KEY, token);
}

/**
 * Clear portal session token
 */
export function clearPortalToken(): void {
  localStorage.removeItem(PORTAL_TOKEN_KEY);
}

/**
 * Get portal auth headers (uses session token, not Supabase JWT)
 */
function getPortalAuthHeaders(): HeadersInit {
  const token = getPortalToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

/**
 * Portal API request helper (uses portal session token via cookie)
 */
async function portalApiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const headers = getPortalAuthHeaders();
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: { ...headers, ...options.headers },
    credentials: 'include',  // Required for cross-origin cookie sending
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Request failed');
  }

  return response.json();
}

/**
 * Portal login (separate from internal auth)
 */
export async function portalLogin(credentials: PortalLoginRequest): Promise<PortalLoginResponse> {
  const response = await fetch(`${API_BASE}/api/portal/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',  // Important for cookie handling
    body: JSON.stringify(credentials),
  });

  const data = await response.json();

  // Backend returns session_token directly on success, or detail on error
  if (response.ok && data.session_token) {
    setPortalToken(data.session_token);
    return { ...data, success: true };
  }

  // Return error response
  return {
    ...data,
    success: false,
    error: data.detail || 'Login failed'
  };
}

/**
 * Portal logout
 */
export async function portalLogout(): Promise<void> {
  try {
    await portalApiRequest('/api/portal/auth/logout', { method: 'POST' });
  } finally {
    clearPortalToken();
  }
}

/**
 * Get current portal user
 * Returns PortalUser directly (not PortalLoginResponse)
 */
export async function getPortalCurrentUser(): Promise<PortalUser> {
  return portalApiRequest('/api/portal/auth/me');
}

/**
 * Get list of available clients (staff only)
 * Used for the client switcher dropdown
 */
export async function getPortalAvailableClients(): Promise<PortalClientSummary[]> {
  return portalApiRequest('/api/portal/clients');
}

/**
 * Switch to a different client (staff only)
 * Updates the session's acting_as_client_id
 */
export async function switchPortalClient(clientId: string): Promise<PortalUser> {
  return portalApiRequest('/api/portal/auth/switch-client', {
    method: 'POST',
    body: JSON.stringify({ client_id: clientId }),
  });
}

/**
 * Get portal dashboard data
 */
export async function getPortalDashboard(): Promise<PortalDashboard> {
  return portalApiRequest('/api/portal/dashboard');
}

/**
 * Get pending deliverables for approval
 * Backend returns array directly, not wrapped in { deliverables: [], total: number }
 */
export async function getPortalDeliverables(params?: {
  status?: 'pending' | 'approved' | 'rejected' | 'all';
  limit?: number;
  offset?: number;
}): Promise<PendingDeliverable[]> {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set('status', params.status);
  if (params?.limit) searchParams.set('limit', params.limit.toString());
  if (params?.offset) searchParams.set('offset', params.offset.toString());

  const query = searchParams.toString();
  return portalApiRequest(`/api/portal/deliverables${query ? `?${query}` : ''}`);
}

/**
 * Get single deliverable details
 */
export async function getPortalDeliverable(deliverableId: string): Promise<PendingDeliverable> {
  return portalApiRequest(`/api/portal/deliverables/${deliverableId}`);
}

/**
 * Approve a deliverable
 */
export async function approveDeliverable(
  deliverableId: string,
  data: DeliverableApprovalRequest
): Promise<{ success: boolean; deliverable: PendingDeliverable }> {
  return portalApiRequest(`/api/portal/deliverables/${deliverableId}/approve`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Reject a deliverable
 */
export async function rejectDeliverable(
  deliverableId: string,
  data: DeliverableApprovalRequest
): Promise<{ success: boolean; deliverable: PendingDeliverable }> {
  return portalApiRequest(`/api/portal/deliverables/${deliverableId}/reject`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Get client requests
 * Backend returns array directly, not wrapped
 */
export async function getPortalRequests(params?: {
  status?: string;
  type?: string;
  limit?: number;
  offset?: number;
}): Promise<ClientRequest[]> {
  const searchParams = new URLSearchParams();
  if (params?.status && params.status !== 'all') searchParams.set('status', params.status);
  if (params?.type && params.type !== 'all') searchParams.set('type', params.type);
  if (params?.limit) searchParams.set('limit', params.limit.toString());
  if (params?.offset) searchParams.set('offset', params.offset.toString());

  const query = searchParams.toString();
  return portalApiRequest(`/api/portal/requests${query ? `?${query}` : ''}`);
}

/**
 * Submit a new request
 * Backend returns ClientRequest directly
 */
export async function submitPortalRequest(
  request: CreateRequestForm
): Promise<ClientRequest> {
  return portalApiRequest('/api/portal/requests', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Get weekly reports
 * Backend returns array directly
 */
export async function getPortalReports(params?: {
  limit?: number;
  offset?: number;
}): Promise<WeeklyReport[]> {
  const searchParams = new URLSearchParams();
  if (params?.limit) searchParams.set('limit', params.limit.toString());
  if (params?.offset) searchParams.set('offset', params.offset.toString());

  const query = searchParams.toString();
  return portalApiRequest(`/api/portal/reports${query ? `?${query}` : ''}`);
}

/**
 * Get portal performance dashboard data
 * Shows marketing metrics without cost data
 */
export interface PerformancePlatformMetrics {
  platform: string;
  impressions: number;
  clicks: number;
  conversions: number;
  reach: number;
  engagements: number;
  video_views: number;
}

export interface PerformanceGBPMetrics {
  impressions: number;
  calls: number;
  directions: number;
  website_clicks: number;
}

export interface PerformanceOverview {
  period: string;
  period_days: number;
  total_reach: number;
  total_website_visits: number;
  total_actions: number;
}

export interface PerformanceDashboardData {
  overview: PerformanceOverview;
  google_ads: PerformancePlatformMetrics | null;
  facebook_ads: PerformancePlatformMetrics | null;
  google_business_profile: PerformanceGBPMetrics | null;
  ga4_sessions: number;
  ga4_users: number;
  has_data: boolean;
}

export async function getPortalPerformance(days: number = 30): Promise<PerformanceDashboardData> {
  return portalApiRequest(`/api/portal/performance?days=${days}`);
}

// =============================================================================
// Portal Meetings
// =============================================================================

import type { PortalMeetingsResponse, PortalCommunicationsResponse, CommunicationContext } from '@/types/portal';

/**
 * Get upcoming meetings for the portal user's client
 */
export async function getPortalMeetings(limit: number = 5): Promise<PortalMeetingsResponse> {
  return portalApiRequest(`/api/portal/meetings?limit=${limit}`);
}

/**
 * Get recent communications (emails + Slack) visible to the portal user
 */
export async function getPortalCommunications(limit: number = 20): Promise<PortalCommunicationsResponse> {
  return portalApiRequest(`/api/portal/communications?limit=${limit}`);
}

/**
 * Get full context for a communication including related items across all channels.
 * Uses semantic search to find related emails, Slack, meetings, tasks within ±7 days.
 */
export async function getCommunicationContext(
  channelType: 'email' | 'slack',
  messageId: string,
  daysRange: number = 7
): Promise<CommunicationContext> {
  return apiRequest(`/api/communications/context/${channelType}/${messageId}?days_range=${daysRange}`);
}

// =============================================================================
// Analytics Dashboard Types (New Interactive Dashboard)
// =============================================================================

export interface AnalyticsPeriodComparison {
  current: number;
  previous: number;
  change_percent: number;
  change_direction: 'up' | 'down' | 'flat';
}

export interface AnalyticsImpactMetric {
  metric_key: string;
  raw_value: number;
  translated_text: string;
  comparison?: AnalyticsPeriodComparison;
  icon?: string;
}

export interface AnalyticsOverviewData {
  period_start: string;
  period_end: string;
  period_days: number;
  store_visits: AnalyticsImpactMetric;
  brand_reach: AnalyticsImpactMetric;
  online_engagement: AnalyticsImpactMetric;
  has_data: boolean;
  data_freshness?: string;
}

export interface AnalyticsTrendPoint {
  date: string;
  value: number;
  label?: string;
}

export interface AnalyticsTrendSeries {
  metric_key: string;
  metric_label: string;
  data: AnalyticsTrendPoint[];
  color?: string;
}

export interface AnalyticsTrendsData {
  period_start: string;
  period_end: string;
  granularity: 'day' | 'week' | 'month';
  series: AnalyticsTrendSeries[];
  comparison_series?: AnalyticsTrendSeries[];
}

export interface AnalyticsServiceMetrics {
  service_name: string;
  service_slug: string;
  metrics: AnalyticsImpactMetric[];
  insight?: string;
}

export interface AnalyticsServiceAttributionData {
  services: AnalyticsServiceMetrics[];
}

export interface AnalyticsLocalMarketCity {
  city: string;
  state?: string;
  sessions: number;
  percentage: number;
  is_local: boolean;
}

export interface AnalyticsLocalMarketData {
  total_sessions: number;
  local_percentage: number;
  cities: AnalyticsLocalMarketCity[];
  insight?: string;
}

export interface AnalyticsSearchQuery {
  query: string;
  clicks: number;
  impressions: number;
  position: number;
  ctr: number;
}

export interface AnalyticsSearchQueriesData {
  queries: AnalyticsSearchQuery[];
  total_clicks: number;
  total_impressions: number;
  insight?: string;
}

export interface AnalyticsTrafficSource {
  source: string;
  medium?: string;
  sessions: number;
  percentage: number;
  color?: string;
}

export interface AnalyticsTrafficSourcesData {
  sources: AnalyticsTrafficSource[];
  total_sessions: number;
  insight?: string;
}

export interface AnalyticsDeliverable {
  id: string;
  name: string;
  type: string;
  thumbnail_url?: string;
  preview_url?: string;
  created_at: string;
}

export interface AnalyticsDeliverablesData {
  deliverables: AnalyticsDeliverable[];
  counts_by_type: Record<string, number>;
  google_drive_folder_url?: string;
}

export interface AnalyticsInsight {
  section: string;
  title: string;
  body: string;
  insight_type: 'positive' | 'neutral' | 'action_needed';
  generated_at: string;
}

export interface AnalyticsInsightsData {
  insights: AnalyticsInsight[];
  industry_report?: {
    title: string;
    summary: string;
    url?: string;
    date: string;
  };
  product_recommendations?: Array<{
    name: string;
    brand: string;
    reason: string;
    seasonal_tag?: string;
  }>;
}

// =============================================================================
// Analytics Dashboard API Functions
// =============================================================================

export async function getPortalAnalyticsOverview(days: number = 30): Promise<AnalyticsOverviewData> {
  return portalApiRequest(`/api/portal/analytics/overview?days=${days}`);
}

export async function getPortalAnalyticsTrends(
  days: number = 30,
  metrics: string[] = ['reach', 'visits', 'actions'],
  granularity: 'day' | 'week' | 'month' = 'day',
  includeComparison: boolean = true
): Promise<AnalyticsTrendsData> {
  const params = new URLSearchParams({
    days: days.toString(),
    metrics: metrics.join(','),
    granularity,
    include_comparison: includeComparison.toString(),
  });
  return portalApiRequest(`/api/portal/analytics/trends?${params}`);
}

export async function getPortalAnalyticsServices(days: number = 30): Promise<AnalyticsServiceAttributionData> {
  return portalApiRequest(`/api/portal/analytics/services?days=${days}`);
}

export async function getPortalAnalyticsLocalMarket(days: number = 30): Promise<AnalyticsLocalMarketData> {
  return portalApiRequest(`/api/portal/analytics/local-market?days=${days}`);
}

export async function getPortalAnalyticsSearchQueries(
  days: number = 30,
  limit: number = 20
): Promise<AnalyticsSearchQueriesData> {
  return portalApiRequest(`/api/portal/analytics/search-queries?days=${days}&limit=${limit}`);
}

export async function getPortalAnalyticsTrafficSources(days: number = 30): Promise<AnalyticsTrafficSourcesData> {
  return portalApiRequest(`/api/portal/analytics/traffic-sources?days=${days}`);
}

export async function getPortalAnalyticsDeliverables(days: number = 30): Promise<AnalyticsDeliverablesData> {
  return portalApiRequest(`/api/portal/analytics/deliverables?days=${days}`);
}

export async function getPortalAnalyticsInsights(
  days: number = 30,
  sections: string[] = ['overview', 'advertising', 'local', 'website']
): Promise<AnalyticsInsightsData> {
  const params = new URLSearchParams({
    days: days.toString(),
    sections: sections.join(','),
  });
  return portalApiRequest(`/api/portal/analytics/insights?${params}`);
}

export interface CacheRefreshResponse {
  rows_affected: number;
  client_slug: string;
  message: string;
}

/**
 * Refresh the Windsor trends cache for the current client.
 * Populates the cache from raw Windsor tables.
 */
export async function refreshPortalAnalyticsCache(days: number = 90): Promise<CacheRefreshResponse> {
  return portalApiRequest(`/api/portal/analytics/refresh-cache?days=${days}`, {
    method: 'POST',
  });
}

// =============================================================================
// Monthly Analytics API
// =============================================================================

export interface MonthlyMetricComparison {
  current_value: number;
  previous_value: number;
  change_percent: number;
  change_direction: 'up' | 'down' | 'flat';
}

export interface MonthlyImpactSummary {
  metric_key: string;
  label: string;
  value: number;
  formatted_value: string;
  comparison: MonthlyMetricComparison;
  icon: string;
  sparkline?: number[];  // Daily values for mini chart
}

export interface BiggestWin {
  title: string;
  metric_value: number;
  formatted_value: string;
  change_percent: number;
  change_direction: string;
  vs_period: string;
  top_driver: string;
  icon: string;
  impact_area: 'local' | 'online' | 'brand';
}

export interface StorytellingContent {
  executive_summary: string | null;
  win_explanations: Array<{ title: string; benefit: string }> | null;
  tab_intros: {
    brand_building?: string;
    online_sales?: string;
    in_store?: string;
  } | null;
}

export interface MonthlyOverviewData {
  month: string;
  month_label: string;
  is_current_month: boolean;
  local_sales: MonthlyImpactSummary;
  brand_visibility: MonthlyImpactSummary;
  online_sales: MonthlyImpactSummary;
  ai_summary: string | null;
  biggest_wins: BiggestWin[];
  has_data: boolean;
  storytelling: StorytellingContent | null;
}

export interface ServiceContribution {
  service_name: string;
  metric_value: number;
  formatted_value: string;
  change_percent: number | null;
  percentage_of_total: number;
}

export interface TopQuery {
  query: string;
  clicks: number;
  impressions: number;
  avg_position: number | null;
}

export interface MonthlyImpactDetailData {
  impact_area: string;
  month: string;
  month_label: string;
  total_value: number;
  formatted_total: string;
  comparison: MonthlyMetricComparison;
  service_attribution: ServiceContribution[];
  trend_data: Array<Record<string, unknown>>;
  top_queries: TopQuery[] | null;
  funnel: FunnelStage[] | null;
}

/**
 * Get monthly analytics overview with MoM comparison.
 */
export async function getPortalMonthlyOverview(month?: string): Promise<MonthlyOverviewData> {
  const params = month ? `?month=${month}` : '';
  return portalApiRequest(`/api/portal/analytics/monthly-overview${params}`);
}

/**
 * Get detailed metrics for a specific impact area.
 */
export async function getPortalMonthlyImpactDetail(
  impactArea: 'local_sales' | 'brand_visibility' | 'online_sales',
  month?: string
): Promise<MonthlyImpactDetailData> {
  const params = month ? `?month=${month}` : '';
  return portalApiRequest(`/api/portal/analytics/monthly-impact/${impactArea}${params}`);
}

export interface ExportReportData {
  client_name: string;
  month_label: string;
  generated_at: string;
  overview: MonthlyOverviewData;
  local_sales: MonthlyImpactDetailData;
  brand_visibility: MonthlyImpactDetailData;
  online_sales: MonthlyImpactDetailData;
}

/**
 * Get all analytics data for PDF export.
 */
export async function getPortalExportReport(month?: string): Promise<ExportReportData> {
  const params = month ? `?month=${month}` : '';
  return portalApiRequest(`/api/portal/analytics/export-report${params}`);
}

export interface ContextualInsightData {
  insight: string;
  overview: string;
  quick_wins: Array<{
    label: string;
    value: string;
    trend?: 'up' | 'down' | 'neutral';
  }>;
  generated_at: string;
}

export async function getPortalContextualInsights(days: number = 30): Promise<ContextualInsightData> {
  return portalApiRequest(`/api/portal/analytics/contextual-insights?days=${days}`);
}

// =============================================================================
// Impact Story API
// =============================================================================

export interface ImpactStoryEvidenceMetric {
  label: string;
  value: number;
  formatted_value: string;
  trend_percent: number | null;
  trend_direction: 'up' | 'down' | 'flat';
  percentage_of_total: number | null;
}

export interface ImpactStoryConnection {
  insight: string;
  explanation: string;
}

export interface ImpactStorySearchTerm {
  term: string;
  clicks: number;
  position: number;
  context: string;
}

export interface ImpactStoryData {
  impact_type: 'in-store' | 'brand' | 'online';
  story: string;
  evidence: ImpactStoryEvidenceMetric[];
  connections: ImpactStoryConnection[];
  meaning: string;
  search_terms: ImpactStorySearchTerm[] | null;
  generated_at: string;
}

/**
 * Fetch AI-generated impact story for a specific impact type.
 */
export async function getPortalImpactStory(
  impactType: 'in-store' | 'brand' | 'online',
  days: number = 30
): Promise<ImpactStoryData> {
  return portalApiRequest(`/api/portal/analytics/impact-story/${impactType}?days=${days}`);
}

// =============================================================================
// Impact Trends API (Time-Series Data)
// =============================================================================

export interface ImpactTrendPoint {
  date: string;
  primary_value: number;
  secondary_value: number | null;
  annotations: string[] | null;
}

/** IC Service attribution - shows which service is driving an impact */
export interface ServiceAttribution {
  service_name: string;           // 'google_ads', 'facebook_ads', 'seo', 'gbp', etc.
  service_display_name: string;   // 'Google Ads', 'Facebook Ads', etc.
  metric_value: number;           // Raw metric value
  metric_label: string;           // 'Impressions', 'Clicks', etc.
  trend_percent: number | null;   // +12.5%
  trend_direction: 'up' | 'down' | 'flat';
  icon: string;                   // Lucide icon name
  color: string;                  // Tailwind color class
}

/** Campaign or content event for timeline annotation */
export interface CampaignEvent {
  date: string;                   // ISO date
  event_type: 'email_send' | 'blog_publish' | 'campaign_start' | 'social_post';
  title: string;                  // "January Newsletter" or "Super Bowl Deals Blog"
  service: string;                // 'email', 'seo', 'facebook_ads', etc.
  metrics: Record<string, unknown> | null;  // {'opens': 450, 'clicks': 89} etc.
}

/** Cross-channel funnel stage for customer journey visualization */
export interface FunnelStage {
  stage_name: string;             // 'awareness', 'website', 'local', 'action', 'visit'
  stage_label: string;            // 'Ad Impressions', 'Website Visits', etc.
  value: number;
  services: string[];             // ['google_ads', 'facebook_ads']
  conversion_rate: number | null; // % converting to next stage
}

/** City breakdown entry for location analysis */
export interface CityBreakdown {
  city: string;
  region: string;
  market_tier: 'local' | 'regional' | 'extended' | 'out_of_market' | 'unknown';
  sessions: number;
  percentage: number;
}

export interface ImpactTrendsData {
  impact_type: 'in-store' | 'brand' | 'online';
  date_range: {
    start: string;
    end: string;
  };
  granularity: 'day' | 'week' | 'month';

  // Primary trend line
  primary_series: ImpactTrendPoint[];
  primary_label: string;

  // Secondary overlay series (for correlations)
  secondary_series: ImpactTrendPoint[] | null;
  secondary_label: string | null;

  // Correlation insights
  correlation_insight: string | null;
  correlation_strength: number | null;

  // Day-of-week breakdown
  day_breakdown: Record<string, number> | null;
  peak_day: string | null;
  peak_day_lift: number | null;

  // NEW: Service attribution - which IC services are driving this impact
  services: ServiceAttribution[] | null;

  // NEW: Campaign events for timeline annotations
  events: CampaignEvent[] | null;

  // NEW: Cross-channel funnel (primarily for online tab)
  funnel: FunnelStage[] | null;

  // NEW: Channel breakdown for stacked charts (brand tab)
  channel_breakdown: Record<string, ImpactTrendPoint[]> | null;

  // NEW: Source breakdown for pie charts (online tab)
  source_breakdown: Record<string, number> | null;

  // NEW: City breakdown for location analysis (online tab)
  city_breakdown: CityBreakdown[] | null;
}

/**
 * Fetch time-series trend data for impact visualizations.
 */
export async function getPortalImpactTrends(
  impactType: 'in-store' | 'brand' | 'online',
  days: number = 30
): Promise<ImpactTrendsData> {
  return portalApiRequest(`/api/portal/analytics/impact-trends/${impactType}?days=${days}`);
}

export interface DashboardWin {
  id: string;
  title: string;
  description: string | null;
  accomplishment_type: string;
  source: string;
  detected_at: string;
}

export interface DashboardWinsData {
  hero_statement: string;
  wins: DashboardWin[];
  generated_at: string;
}

export async function getPortalDashboardWins(days: number = 30): Promise<DashboardWinsData> {
  return portalApiRequest(`/api/portal/dashboard/wins?days=${days}`);
}

// =============================================================================
// Forms API (Form Builder)
// =============================================================================

import type {
  Form,
  FormWithFields,
  FormField,
  FormSubmission,
  FormAnalytics,
  FormWebhook,
  FormsListResponse,
  SubmissionsListResponse,
  PublicFormResponse,
  FormCreateRequest,
  FormUpdateRequest,
  FieldCreateRequest,
  FieldUpdateRequest,
  FieldReorderRequest,
  SubmissionStartRequest,
  SubmissionProgressRequest,
  SubmissionSubmitRequest,
  WebhookCreateRequest,
  WebhookUpdateRequest,
  GenerateFormRequest,
  GeneratedForm,
} from '@/types/forms';

// Re-export types for convenience
export type {
  Form,
  FormWithFields,
  FormField,
  FormSubmission,
  FormAnalytics,
  FormWebhook,
  FormsListResponse,
  SubmissionsListResponse,
};

/**
 * List all forms (authenticated)
 */
export async function getForms(params?: {
  status?: string;
  client_slug?: string;
  limit?: number;
  offset?: number;
}): Promise<FormsListResponse> {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set('status', params.status);
  if (params?.client_slug) searchParams.set('client_slug', params.client_slug);
  if (params?.limit) searchParams.set('limit', params.limit.toString());
  if (params?.offset) searchParams.set('offset', params.offset.toString());

  const query = searchParams.toString();
  return apiRequest(`/api/forms${query ? `?${query}` : ''}`);
}

/**
 * Create a new form
 */
export async function createForm(request: FormCreateRequest): Promise<Form> {
  return apiRequest('/api/forms', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Get a form with all its fields
 */
export async function getForm(formId: string): Promise<FormWithFields> {
  return apiRequest(`/api/forms/${formId}`);
}

/**
 * Update a form
 */
export async function updateForm(
  formId: string,
  request: FormUpdateRequest
): Promise<Form> {
  return apiRequest(`/api/forms/${formId}`, {
    method: 'PATCH',
    body: JSON.stringify(request),
  });
}

/**
 * Delete (archive) a form
 */
export async function deleteForm(formId: string): Promise<{ success: boolean; message: string }> {
  return apiRequest(`/api/forms/${formId}`, {
    method: 'DELETE',
  });
}

/**
 * Duplicate a form
 */
export async function duplicateForm(formId: string): Promise<Form> {
  return apiRequest(`/api/forms/${formId}/duplicate`, {
    method: 'POST',
  });
}

/**
 * Publish a form
 */
export async function publishForm(formId: string): Promise<Form> {
  return apiRequest(`/api/forms/${formId}/publish`, {
    method: 'POST',
  });
}

/**
 * Unpublish a form
 */
export async function unpublishForm(formId: string): Promise<Form> {
  return apiRequest(`/api/forms/${formId}/unpublish`, {
    method: 'POST',
  });
}

// -----------------------------------------------------------------------------
// Form Fields
// -----------------------------------------------------------------------------

/**
 * Add a field to a form
 */
export async function createFormField(
  formId: string,
  request: FieldCreateRequest
): Promise<FormField> {
  return apiRequest(`/api/forms/${formId}/fields`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Update a form field
 */
export async function updateFormField(
  formId: string,
  fieldId: string,
  request: FieldUpdateRequest
): Promise<FormField> {
  return apiRequest(`/api/forms/${formId}/fields/${fieldId}`, {
    method: 'PATCH',
    body: JSON.stringify(request),
  });
}

/**
 * Delete a form field
 */
export async function deleteFormField(
  formId: string,
  fieldId: string
): Promise<{ success: boolean; message: string }> {
  return apiRequest(`/api/forms/${formId}/fields/${fieldId}`, {
    method: 'DELETE',
  });
}

/**
 * Reorder form fields
 */
export async function reorderFormFields(
  formId: string,
  request: FieldReorderRequest
): Promise<{ success: boolean; fields: FormField[] }> {
  return apiRequest(`/api/forms/${formId}/fields/reorder`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

// -----------------------------------------------------------------------------
// Submissions
// -----------------------------------------------------------------------------

/**
 * Get submissions for a form
 */
export async function getFormSubmissions(
  formId: string,
  params?: {
    status?: string;
    page?: number;
    per_page?: number;
  }
): Promise<SubmissionsListResponse> {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set('status', params.status);
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.per_page) searchParams.set('per_page', params.per_page.toString());

  const query = searchParams.toString();
  return apiRequest(`/api/forms/${formId}/submissions${query ? `?${query}` : ''}`);
}

/**
 * Get a single submission
 */
export async function getFormSubmission(
  formId: string,
  submissionId: string
): Promise<FormSubmission> {
  return apiRequest(`/api/forms/${formId}/submissions/${submissionId}`);
}

/**
 * Delete a submission
 */
export async function deleteFormSubmission(
  formId: string,
  submissionId: string
): Promise<{ success: boolean; message: string }> {
  return apiRequest(`/api/forms/${formId}/submissions/${submissionId}`, {
    method: 'DELETE',
  });
}

/**
 * Export submissions as CSV
 */
export async function exportFormSubmissions(
  formId: string,
  format: 'csv' | 'xlsx' = 'csv'
): Promise<Blob> {
  const headers = await getAuthHeaders();
  const response = await fetch(
    `${config.apiUrl}/api/forms/${formId}/submissions/export?format=${format}`,
    { headers, credentials: 'include' }
  );
  if (!response.ok) {
    throw new Error('Failed to export submissions');
  }
  return response.blob();
}

// -----------------------------------------------------------------------------
// Analytics
// -----------------------------------------------------------------------------

/**
 * Get form analytics
 */
export async function getFormAnalytics(
  formId: string,
  params?: {
    start_date?: string;
    end_date?: string;
  }
): Promise<FormAnalytics> {
  const searchParams = new URLSearchParams();
  if (params?.start_date) searchParams.set('start_date', params.start_date);
  if (params?.end_date) searchParams.set('end_date', params.end_date);

  const query = searchParams.toString();
  return apiRequest(`/api/forms/${formId}/analytics${query ? `?${query}` : ''}`);
}

// -----------------------------------------------------------------------------
// Webhooks
// -----------------------------------------------------------------------------

/**
 * Get webhooks for a form
 */
export async function getFormWebhooks(formId: string): Promise<{ webhooks: FormWebhook[] }> {
  return apiRequest(`/api/forms/${formId}/webhooks`);
}

/**
 * Create a webhook
 */
export async function createFormWebhook(
  formId: string,
  request: WebhookCreateRequest
): Promise<FormWebhook> {
  return apiRequest(`/api/forms/${formId}/webhooks`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Update a webhook
 */
export async function updateFormWebhook(
  formId: string,
  webhookId: string,
  request: WebhookUpdateRequest
): Promise<FormWebhook> {
  return apiRequest(`/api/forms/${formId}/webhooks/${webhookId}`, {
    method: 'PATCH',
    body: JSON.stringify(request),
  });
}

/**
 * Delete a webhook
 */
export async function deleteFormWebhook(
  formId: string,
  webhookId: string
): Promise<{ success: boolean; message: string }> {
  return apiRequest(`/api/forms/${formId}/webhooks/${webhookId}`, {
    method: 'DELETE',
  });
}

/**
 * Test a webhook
 */
export async function testFormWebhook(
  formId: string,
  webhookId: string
): Promise<{ success: boolean; status_code: number; response: string }> {
  return apiRequest(`/api/forms/${formId}/webhooks/${webhookId}/test`, {
    method: 'POST',
  });
}

// -----------------------------------------------------------------------------
// AI Features
// -----------------------------------------------------------------------------

/**
 * Generate a form using AI
 */
export async function generateForm(request: GenerateFormRequest): Promise<GeneratedForm> {
  return apiRequest('/api/forms/generate', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

// -----------------------------------------------------------------------------
// QR Code
// -----------------------------------------------------------------------------

/**
 * Get QR code for a form
 */
export async function getFormQRCode(
  formId: string,
  params?: {
    size?: number;
    format?: 'png' | 'svg';
  }
): Promise<Blob> {
  const searchParams = new URLSearchParams();
  if (params?.size) searchParams.set('size', params.size.toString());
  if (params?.format) searchParams.set('format', params.format);

  const query = searchParams.toString();
  const headers = await getAuthHeaders();
  const response = await fetch(
    `${config.apiUrl}/api/forms/${formId}/qr${query ? `?${query}` : ''}`,
    { headers, credentials: 'include' }
  );
  if (!response.ok) {
    throw new Error('Failed to generate QR code');
  }
  return response.blob();
}

// -----------------------------------------------------------------------------
// Public Endpoints (No Auth Required)
// -----------------------------------------------------------------------------

/**
 * Get a public form for display (no auth)
 */
export async function getPublicForm(slug: string): Promise<PublicFormResponse> {
  const response = await fetch(`${config.apiUrl}/api/forms/public/${slug}`);
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Form not found' }));
    throw new Error(error.detail || error.error || 'Form not found');
  }
  return response.json();
}

/**
 * Start a form submission (creates partial submission)
 */
export async function startFormSubmission(
  slug: string,
  request: SubmissionStartRequest
): Promise<{ submission_id: string; session_id: string }> {
  const response = await fetch(`${config.apiUrl}/api/forms/public/${slug}/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to start submission' }));
    throw new Error(error.detail || error.error || 'Failed to start submission');
  }
  return response.json();
}

/**
 * Save submission progress
 */
export async function saveFormProgress(
  slug: string,
  submissionId: string,
  request: SubmissionProgressRequest
): Promise<{ success: boolean }> {
  const response = await fetch(`${config.apiUrl}/api/forms/public/${slug}/progress`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ submission_id: submissionId, ...request }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to save progress' }));
    throw new Error(error.detail || error.error || 'Failed to save progress');
  }
  return response.json();
}

/**
 * Submit a completed form
 */
export async function submitForm(
  slug: string,
  submissionId: string,
  request: SubmissionSubmitRequest
): Promise<{ success: boolean; submission_id: string }> {
  const response = await fetch(`${config.apiUrl}/api/forms/public/${slug}/submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ submission_id: submissionId, ...request }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to submit form' }));
    throw new Error(error.detail || error.error || 'Failed to submit form');
  }
  return response.json();
}

/**
 * Track form view/event (for analytics)
 */
export async function trackFormEvent(
  slug: string,
  event: 'view' | 'start' | 'submit',
  data?: Record<string, any>
): Promise<void> {
  await fetch(`${config.apiUrl}/api/forms/public/${slug}/track`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ event, ...data }),
  });
}

/**
 * Upload a file for a form field
 */
export async function uploadFormFile(
  slug: string,
  file: File,
  fieldKey: string,
  sessionId: string,
  onProgress?: (percent: number) => void
): Promise<{ url: string; filename: string; size: number; content_type: string }> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('field_key', fieldKey);
  formData.append('session_id', sessionId);

  const response = await fetch(`${config.apiUrl}/api/forms/public/${slug}/upload`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Upload failed' }));
    throw new Error(error.detail || error.error || 'Upload failed');
  }

  return response.json();
}

/**
 * Scrape and analyze a portfolio URL
 */
export async function scrapePortfolioUrl(
  slug: string,
  url: string,
  fieldKey: string,
  sessionId: string
): Promise<{ success: boolean; url: string; analysis?: any; error?: string }> {
  const response = await fetch(`${config.apiUrl}/api/forms/public/${slug}/scrape-portfolio`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url, field_key: fieldKey, session_id: sessionId }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Scrape failed' }));
    throw new Error(error.detail || error.error || 'Portfolio scrape failed');
  }

  return response.json();
}

// =============================================================================
// Client Management API (Admin Dashboard)
// =============================================================================

export interface ClientListItem {
  id: string;
  display_name: string;
  slug: string;
  status: 'active' | 'paused' | 'churned';
  health_score: number | null;
  health_status: 'critical' | 'warning' | 'good' | 'excellent' | null;
  services_count: number;
  last_activity_at: string | null;
  contract_renewal_date: string | null;
  logo_url: string | null;
  created_at: string;
}

export interface ClientsListResponse {
  clients: ClientListItem[];
  total: number;
}

export interface HealthDashboardResponse {
  summary: {
    total_clients: number;
    healthy_count: number;
    warning_count: number;
    critical_count: number;
    avg_health_score: number;
  };
  at_risk_clients: {
    id: string;
    display_name: string;
    health_score: number;
    health_status: 'critical' | 'warning';
    risk_reasons: string[];
    days_since_last_contact: number;
    overdue_tasks: number;
    blocked_tasks: number;
  }[];
  health_distribution: {
    excellent: number;
    good: number;
    warning: number;
    critical: number;
  };
}

// --- Client Connections ---

export interface ConnectionOwner {
  contact_id: string | null;
  name: string | null;
  email: string | null;
  role: string | null;
}

export interface ConnectionContract {
  exists: boolean;
  signed_date: string | null;
  google_drive_url: string | null;
}

export interface ConnectionSlackChannel {
  exists: boolean;
  channel_name: string | null;
}

export interface ConnectionDocument {
  exists: boolean;
  google_doc_url: string | null;
  created_at: string | null;
}

export interface ConnectionFathomCall {
  exists: boolean;
  url: string | null;
}

export interface ClientConnectionRow {
  id: string;
  slug: string;
  display_name: string;
  // General
  owner: ConnectionOwner | null;
  phone: string | null;
  emails: string[];
  website_url: string | null;
  address: string | null;
  google_drive_url: string | null;
  contract: ConnectionContract;
  slack_channel: ConnectionSlackChannel;
  fathom_sales_call: ConnectionFathomCall;
  fathom_onboarding_call: ConnectionFathomCall;
  brand_guide_url: string | null;
  liquorchat_channel: string | null;
  // Assets
  profile: ConnectionDocument;
  content_pillars: ConnectionDocument;
  twelve_week_calendar: ConnectionDocument;
  // Services
  services_count: number;
  services_list: string[];
  // Connected Accounts
  google_analytics: boolean;
  google_business: boolean;
  google_search_console: boolean;
  google_merchant: boolean;
  google_ads: boolean;
  facebook_ads: boolean;
  instagram_organic: boolean;
  is_internal: boolean;
}

export interface ClientConnectionsResponse {
  clients: ClientConnectionRow[];
  internal_clients: ClientConnectionRow[];
  total: number;
  summary: {
    total_clients: number;
    with_owner: number;
    with_contract: number;
    with_slack: number;
    with_profile: number;
    with_pillars: number;
    with_calendar: number;
    with_any_windsor: number;
    with_liquorchat: number;
  };
}

export interface AgencyService {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  is_active: boolean;
  client_count: number;
  created_at: string;
}

export interface ServicePreset {
  id: string;
  name: string;
  description: string | null;
  services: string[];
  created_at: string;
}

export interface ServiceCreateRequest {
  name: string;
  description?: string;
  category?: string;
}

export interface ServiceUpdateRequest {
  name?: string;
  description?: string;
  category?: string;
  is_active?: boolean;
}

export interface PresetCreateRequest {
  name: string;
  description?: string;
  services: string[];
}

export interface ClientWeeklyReport {
  id: string;
  client_id: string;
  client_name: string;
  week_start: string;
  week_end: string;
  google_doc_url: string | null;
  summary: {
    tasks_completed: number;
    wins: string[];
    next_steps: string[];
  } | null;
  created_at: string;
}

export interface AgencySettings {
  id: string;
  agency_name: string;
  logo_url: string | null;
  submit_request_url: string | null;
  liquorchat_url: string | null;
  slack_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface QuickAction {
  id: string;
  label: string;
  url: string;
  button_style: 'primary' | 'secondary' | 'gradient';
  sort_order: number;
  is_global: boolean;
  client_id: string | null;
  created_at: string;
}

/**
 * Get list of clients with health scores
 */
export async function getClients(params?: {
  status?: 'active' | 'paused' | 'churned' | 'all';
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<ClientsListResponse> {
  const searchParams = new URLSearchParams();
  if (params?.status && params.status !== 'all') searchParams.set('status', params.status);
  if (params?.search) searchParams.set('search', params.search);
  if (params?.limit) searchParams.set('limit', params.limit.toString());
  if (params?.offset) searchParams.set('offset', params.offset.toString());

  const query = searchParams.toString();
  return apiRequest(`/api/agency/clients${query ? `?${query}` : ''}`);
}

/**
 * Get health dashboard data
 */
export async function getHealthDashboard(): Promise<HealthDashboardResponse> {
  return apiRequest('/api/agency/health-dashboard');
}

/**
 * Get client connections matrix
 */
export async function getClientConnections(): Promise<ClientConnectionsResponse> {
  return apiRequest('/api/agency/client-connections');
}

/**
 * Get list of services
 */
export async function getServices(): Promise<{ services: AgencyService[] }> {
  return apiRequest('/api/agency/services');
}

/**
 * Create a new service
 */
export async function createService(request: ServiceCreateRequest): Promise<AgencyService> {
  return apiRequest('/api/agency/services', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Update a service
 */
export async function updateService(
  serviceId: string,
  request: ServiceUpdateRequest
): Promise<AgencyService> {
  return apiRequest(`/api/agency/services/${serviceId}`, {
    method: 'PATCH',
    body: JSON.stringify(request),
  });
}

/**
 * Delete a service
 */
export async function deleteService(serviceId: string): Promise<{ success: boolean; message: string }> {
  return apiRequest(`/api/agency/services/${serviceId}`, {
    method: 'DELETE',
  });
}

/**
 * Get service presets
 */
export async function getServicePresets(): Promise<{ presets: ServicePreset[] }> {
  return apiRequest('/api/agency/presets');
}

/**
 * Create a service preset
 */
export async function createServicePreset(request: PresetCreateRequest): Promise<ServicePreset> {
  return apiRequest('/api/agency/presets', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Delete a service preset
 */
export async function deleteServicePreset(presetId: string): Promise<{ success: boolean; message: string }> {
  return apiRequest(`/api/agency/presets/${presetId}`, {
    method: 'DELETE',
  });
}

/**
 * Get weekly reports
 */
export async function getWeeklyReports(params?: {
  client_id?: string;
  limit?: number;
  offset?: number;
}): Promise<{ reports: ClientWeeklyReport[]; total: number }> {
  const searchParams = new URLSearchParams();
  if (params?.client_id) searchParams.set('client_id', params.client_id);
  if (params?.limit) searchParams.set('limit', params.limit.toString());
  if (params?.offset) searchParams.set('offset', params.offset.toString());

  const query = searchParams.toString();
  return apiRequest(`/api/agency/reports${query ? `?${query}` : ''}`);
}

/**
 * Generate weekly report for a client (or all clients)
 */
export async function generateWeeklyReport(params?: {
  client_id?: string;
  all_clients?: boolean;
}): Promise<{ success: boolean; reports_generated: number; report_ids: string[] }> {
  return apiRequest('/api/agency/reports/generate', {
    method: 'POST',
    body: JSON.stringify(params || {}),
  });
}

/**
 * Get agency settings
 */
export async function getAgencySettings(): Promise<AgencySettings> {
  return apiRequest('/api/agency/settings');
}

/**
 * Update agency settings
 */
export async function updateAgencySettings(
  updates: Partial<Omit<AgencySettings, 'id' | 'created_at' | 'updated_at'>>
): Promise<AgencySettings> {
  return apiRequest('/api/agency/settings', {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

/**
 * Get quick actions
 */
export async function getQuickActions(params?: {
  global_only?: boolean;
  client_id?: string;
}): Promise<{ actions: QuickAction[] }> {
  const searchParams = new URLSearchParams();
  if (params?.global_only) searchParams.set('global_only', 'true');
  if (params?.client_id) searchParams.set('client_id', params.client_id);

  const query = searchParams.toString();
  return apiRequest(`/api/agency/quick-actions${query ? `?${query}` : ''}`);
}

/**
 * Create a quick action
 */
export async function createQuickAction(request: {
  label: string;
  url: string;
  button_style?: 'primary' | 'secondary' | 'gradient';
  is_global?: boolean;
  client_id?: string;
}): Promise<QuickAction> {
  return apiRequest('/api/agency/quick-actions', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Update a quick action
 */
export async function updateQuickAction(
  actionId: string,
  request: Partial<{
    label: string;
    url: string;
    button_style: 'primary' | 'secondary' | 'gradient';
    sort_order: number;
  }>
): Promise<QuickAction> {
  return apiRequest(`/api/agency/quick-actions/${actionId}`, {
    method: 'PATCH',
    body: JSON.stringify(request),
  });
}

/**
 * Delete a quick action
 */
export async function deleteQuickAction(actionId: string): Promise<{ success: boolean; message: string }> {
  return apiRequest(`/api/agency/quick-actions/${actionId}`, {
    method: 'DELETE',
  });
}

/**
 * Reorder quick actions
 */
export async function reorderQuickActions(actionIds: string[]): Promise<{ success: boolean; message: string }> {
  return apiRequest('/api/agency/quick-actions/reorder', {
    method: 'POST',
    body: JSON.stringify({ action_ids: actionIds }),
  });
}

// =============================================================================
// Client Weekly Services (Production Board Deliverables)
// =============================================================================

export interface ClientWeeklyService {
  id: string;
  client_slug: string;
  service_type: string;
  display_name: string;
  sort_order: number;
  is_active: boolean;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface WeekExpectedDeliverable {
  id: string;
  service_type: string;
  display_name: string;
  status: 'missing' | 'linked' | 'completed';
  asset_id?: string;
  sort_order: number;
}

export interface WeekDeliverablesSummary {
  client_slug: string;
  client_display_name?: string;
  total_expected: number;
  completed_count: number;
  in_progress_count: number;
  missing_count: number;
  deliverables: WeekExpectedDeliverable[];
}

/**
 * Get weekly services for a client
 */
export async function getClientWeeklyServices(clientSlug: string): Promise<{ services: ClientWeeklyService[] }> {
  return apiRequest(`/api/agency-admin/clients/${clientSlug}/weekly-services`);
}

/**
 * Create a weekly service for a client
 */
export async function createClientWeeklyService(
  clientSlug: string,
  request: { service_type: string; display_name: string; sort_order?: number; notes?: string }
): Promise<ClientWeeklyService> {
  return apiRequest(`/api/agency-admin/clients/${clientSlug}/weekly-services`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Update a weekly service
 */
export async function updateClientWeeklyService(
  clientSlug: string,
  serviceId: string,
  request: Partial<{ display_name: string; sort_order: number; is_active: boolean; notes: string }>
): Promise<ClientWeeklyService> {
  return apiRequest(`/api/agency-admin/clients/${clientSlug}/weekly-services/${serviceId}`, {
    method: 'PATCH',
    body: JSON.stringify(request),
  });
}

/**
 * Delete a weekly service
 */
export async function deleteClientWeeklyService(
  clientSlug: string,
  serviceId: string
): Promise<{ success: boolean; message: string }> {
  return apiRequest(`/api/agency-admin/clients/${clientSlug}/weekly-services/${serviceId}`, {
    method: 'DELETE',
  });
}

/**
 * Get week deliverables summary
 */
export async function getWeekDeliverablesSummary(weekId: string): Promise<{ data: WeekDeliverablesSummary[] }> {
  return apiRequest(`/api/agency-admin/weeks/${weekId}/deliverables-summary`);
}

/**
 * Generate expected deliverables for a week (if not already generated)
 */
export async function generateWeekExpectedDeliverables(weekId: string): Promise<{ success: boolean; generated: number }> {
  return apiRequest(`/api/agency-admin/weeks/${weekId}/generate-expected-deliverables`, {
    method: 'POST',
  });
}

/**
 * Link assets to expected deliverables for a week
 */
export async function linkAssetsToDeliverables(weekId: string): Promise<{ success: boolean; linked: number }> {
  return apiRequest(`/api/agency-admin/weeks/${weekId}/link-assets-to-deliverables`, {
    method: 'POST',
  });
}

/**
 * Backfill: Link assets to weeks by date
 */
export async function backfillLinkAssetsToWeeks(): Promise<{ success: boolean; linked: number }> {
  return apiRequest('/api/agency-admin/backfill/link-assets-to-weeks', {
    method: 'POST',
  });
}

/**
 * Client Detail Types
 */
export interface ClientDetailService {
  id: string;
  name: string;
  status: 'active' | 'paused';
  external_link?: string;
}

export interface ClientActivityItem {
  id: string;
  type: 'call' | 'request' | 'deliverable' | 'report';
  title: string;
  description?: string;
  date: string;
  status?: string;
}

export interface ClientTask {
  id: string;
  title: string;
  status: string;
  priority: string;
  assignee?: string;
  due_date?: string;
  qa_stage?: string;
}

export interface ClientMarketingSummary {
  google_ads?: {
    impressions: number;
    clicks: number;
    conversions: number;
    trend: 'up' | 'down' | 'stable';
  };
  facebook?: {
    reach: number;
    engagements: number;
    trend: 'up' | 'down' | 'stable';
  };
  ga4?: {
    sessions: number;
    users: number;
    bounce_rate: number;
    trend: 'up' | 'down' | 'stable';
  };
  gbp?: {
    impressions: number;
    calls: number;
    directions: number;
    trend: 'up' | 'down' | 'stable';
  };
  last_synced?: string;
}

export interface CRMContact {
  id: string;
  full_name?: string;
  email?: string;
  phone?: string;
  linkedin_url?: string;
  role?: string;
  is_primary: boolean;
  job_title?: string;
}

export interface ClientMeeting {
  id: string;
  title: string;
  date: string;
  summary?: string;
  call_type?: string;
  google_doc_url?: string;
}

export interface ClientDetail extends ClientListItem {
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  address?: string;
  website?: string;
  // Business info
  business_hours?: Record<string, { open?: string; close?: string }>;
  google_places_id?: string;
  location?: string;
  // Account manager
  csm_name?: string;
  csm_email?: string;
  contract_start_date?: string;
  // Branding
  branding_guide_url?: string;
  // CRM contacts
  crm_contacts?: CRMContact[];
  // Meetings by type
  sales_calls?: ClientMeeting[];
  onboarding_calls?: ClientMeeting[];
  // Related data
  services?: ClientDetailService[];
  recent_activity?: ClientActivityItem[];
  active_tasks?: ClientTask[];
  marketing_summary?: ClientMarketingSummary;
  deliverables?: ClientDeliverable[];
  // Portal content (editable)
  help_content?: string;
  adhoc_policy?: string;
  timeline_policy?: string;
  contact_policy?: string;
  liquorchat_url?: string;
  google_drive_url?: string;
  portal_channel_id?: string;
  portal_channel_name?: string;
}

/**
 * Client Deliverable type
 */
export interface ClientDeliverable {
  id: string;
  title: string;
  description?: string;
  category: 'social_post' | 'newsletter' | 'flyer' | 'website' | 'ad_creative' | 'video' | 'other';
  thumbnail_url?: string;
  file_urls?: string[];
  delivered_at: string;
  requires_approval: boolean;
  client_approved?: boolean | null;
  client_approval_at?: string;
  client_feedback?: string;
  is_visible_to_client: boolean;
}

/**
 * Client Request type
 */
export interface ClientRequest {
  id: string;
  request_type: 'ad_hoc' | 'revision' | 'new_service' | 'feedback' | 'other';
  title: string;
  description: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  status: 'pending' | 'acknowledged' | 'in_progress' | 'completed' | 'cancelled';
  submitted_at: string;
  acknowledged_at?: string;
  completed_at?: string;
  submitted_by_name?: string;
}

/**
 * Get detailed client information
 */
export async function getClientDetail(clientId: string): Promise<ClientDetail> {
  return apiRequest(`/api/agency/clients/${clientId}/detail`);
}

/**
 * Update client data for a client
 */
export interface UpdateClientData {
  display_name?: string;
  status?: 'active' | 'paused' | 'churned';
  website_url?: string;
  address?: string;
  city?: string;
  state?: string;
  phone?: string;
  hours?: string;
  logo_url?: string;
  header_gradient?: string;
  slack_channel_url?: string;
  contract_end_date?: string;
  contact_name?: string;
  contact_email?: string;
  primary_color?: string;
  secondary_color?: string;
  // Portal content fields
  help_content?: string;
  adhoc_policy?: string;
  timeline_policy?: string;
  contact_policy?: string;
  liquorchat_url?: string;
  google_drive_url?: string;
  portal_channel_id?: string | null;
}

export interface TeamChannelSearchResult {
  id: string;
  name: string;
  channel_type: string;
  client_slug: string | null;
  description: string | null;
}

export async function searchTeamChannels(query: string): Promise<{ channels: TeamChannelSearchResult[] }> {
  const params = new URLSearchParams({ q: query });
  return apiRequest(`/api/agency/team-channels/search?${params}`);
}

export async function updateClient(
  clientId: string,
  data: UpdateClientData
): Promise<{ success: boolean; client: ClientListItem }> {
  return apiRequest(`/api/agency/clients/${clientId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/**
 * Get client deliverables
 */
export async function getClientDeliverables(clientId: string): Promise<ClientDeliverable[]> {
  return apiRequest(`/api/agency/clients/${clientId}/deliverables`);
}

/**
 * Create client deliverable
 */
export async function createClientDeliverable(
  clientId: string,
  data: Omit<ClientDeliverable, 'id' | 'delivered_at' | 'client_approved' | 'client_approval_at' | 'client_feedback'>
): Promise<ClientDeliverable> {
  return apiRequest(`/api/agency/clients/${clientId}/deliverables`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Update client deliverable approval status
 */
export async function updateDeliverableApproval(
  deliverableId: string,
  approved: boolean,
  feedback?: string
): Promise<ClientDeliverable> {
  return apiRequest(`/api/agency/deliverables/${deliverableId}/approval`, {
    method: 'PATCH',
    body: JSON.stringify({ approved, feedback }),
  });
}

/**
 * Update client deliverable
 */
export async function updateClientDeliverable(
  deliverableId: string,
  data: Partial<Omit<ClientDeliverable, 'id' | 'delivered_at' | 'client_approved' | 'client_approval_at' | 'client_feedback'>>
): Promise<ClientDeliverable> {
  return apiRequest(`/api/agency/deliverables/${deliverableId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/**
 * Delete client deliverable
 */
export async function deleteClientDeliverable(
  deliverableId: string
): Promise<{ success: boolean; deleted_id: string }> {
  return apiRequest(`/api/agency/deliverables/${deliverableId}`, {
    method: 'DELETE',
  });
}

/**
 * Production Asset File (from production board)
 */
export interface ProductionAssetFile {
  id: string;
  file_name: string;
  file_url?: string;
  thumbnail_url?: string;
  mime_type?: string;
  width?: number;
  height?: number;
}

/**
 * Production Asset (from production board)
 */
export interface ProductionAsset {
  id: string;
  asset_name: string;
  asset_type?: string;
  asset_date?: string;
  production_status?: string;
  client_approval_status?: string;
  qa_status?: string;
  artist_assigned_name?: string;
  due_date?: string;
  week_label?: string;
  brief_headline?: string;
  brief_summary?: string;
  version_count: number;
  latest_thumbnail_url?: string;
  latest_files: ProductionAssetFile[];
  last_sent_to_client_at?: string;
  created_at?: string;
}

/**
 * Get production assets for a client (from production board)
 */
export async function getClientProductionAssets(
  clientSlug: string,
  options?: { limit?: number; status?: string }
): Promise<ProductionAsset[]> {
  const params = new URLSearchParams();
  if (options?.limit) params.append('limit', options.limit.toString());
  if (options?.status) params.append('status', options.status);
  const query = params.toString() ? `?${params.toString()}` : '';
  return apiRequest(`/api/agency/clients/${clientSlug}/production-assets${query}`);
}

/**
 * Client Event from Command Center
 */
export interface ClientEvent {
  id: string;
  client_slug: string;
  event_type: string;
  event_category: string;
  title: string;
  description?: string;
  sentiment?: 'positive' | 'negative' | 'neutral';
  event_at: string;
  metadata?: Record<string, unknown>;
  created_at: string;
}

/**
 * Client Task from Command Center
 */
export interface ClientTask {
  id: string;
  title: string;
  description?: string;
  status: string;
  priority: string;
  owner?: string;
  due_date?: string;
  source?: string;
  created_at: string;
  updated_at?: string;
}

/**
 * Get client events from Command Center
 */
export async function getClientEvents(
  clientSlug: string,
  options?: { category?: string; days?: number; limit?: number }
): Promise<{ events: ClientEvent[]; total: number }> {
  const params = new URLSearchParams();
  if (options?.category) params.append('category', options.category);
  if (options?.days) params.append('days', options.days.toString());
  if (options?.limit) params.append('limit', options.limit.toString());
  const query = params.toString() ? `?${params.toString()}` : '';
  return apiRequest(`/api/command-center/clients/${clientSlug}/events${query}`);
}

/**
 * Get client tasks from Command Center
 */
export async function getClientTasks(
  clientSlug: string,
  options?: { status?: string; limit?: number }
): Promise<{ tasks: ClientTask[]; total: number }> {
  const params = new URLSearchParams();
  if (options?.status) params.append('status', options.status);
  if (options?.limit) params.append('limit', options.limit.toString());
  const query = params.toString() ? `?${params.toString()}` : '';
  return apiRequest(`/api/command-center/clients/${clientSlug}/tasks${query}`);
}

/**
 * Client Meeting from Command Center
 */
export interface ClientMeetingRecord {
  id: string;
  title: string;
  meeting_date: string;
  duration_minutes?: number;
  summary?: string;
  pain_points?: string[];
  google_doc_url?: string;
}

/**
 * Get client meetings from Command Center
 */
export async function getClientMeetings(
  clientSlug: string,
  options?: { days?: number; limit?: number; offset?: number }
): Promise<{ meetings: ClientMeetingRecord[]; total: number }> {
  const params = new URLSearchParams();
  if (options?.days) params.append('days', options.days.toString());
  if (options?.limit) params.append('limit', options.limit.toString());
  if (options?.offset) params.append('offset', options.offset.toString());
  const query = params.toString() ? `?${params.toString()}` : '';
  return apiRequest(`/api/command-center/clients/${clientSlug}/meetings${query}`);
}

/**
 * Get client requests
 */
export async function getClientRequests(clientId: string): Promise<ClientRequest[]> {
  return apiRequest(`/api/agency/clients/${clientId}/requests`);
}

/**
 * Update client request status
 */
export async function updateClientRequestStatus(
  requestId: string,
  status: ClientRequest['status']
): Promise<ClientRequest> {
  return apiRequest(`/api/agency/requests/${requestId}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

// =============================================================================
// Form Folders API
// =============================================================================

import type {
  FormFolder,
  FolderCreateRequest,
  FolderUpdateRequest,
  FormTemplate,
  TemplateCategory,
} from '@/types/forms';

export type { FormFolder, FormTemplate, TemplateCategory };

/**
 * List all form folders
 */
export async function getFormFolders(): Promise<{ folders: FormFolder[] }> {
  return apiRequest('/api/forms/folders');
}

/**
 * Create a new form folder
 */
export async function createFormFolder(request: FolderCreateRequest): Promise<FormFolder> {
  return apiRequest('/api/forms/folders', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Update a form folder
 */
export async function updateFormFolder(
  folderId: string,
  request: FolderUpdateRequest
): Promise<FormFolder> {
  return apiRequest(`/api/forms/folders/${folderId}`, {
    method: 'PATCH',
    body: JSON.stringify(request),
  });
}

/**
 * Delete a form folder
 */
export async function deleteFormFolder(folderId: string): Promise<{ success: boolean; message: string }> {
  return apiRequest(`/api/forms/folders/${folderId}`, {
    method: 'DELETE',
  });
}

/**
 * Move a form to a folder
 */
export async function moveFormToFolder(
  formId: string,
  folderId: string | null
): Promise<{ success: boolean; form_id: string; folder_id: string | null }> {
  return apiRequest(`/api/forms/${formId}/move`, {
    method: 'POST',
    body: JSON.stringify({ folder_id: folderId }),
  });
}

// =============================================================================
// Form Templates API
// =============================================================================

/**
 * List all form templates
 */
export async function getFormTemplates(params?: {
  category?: TemplateCategory;
  is_featured?: boolean;
}): Promise<{ templates: FormTemplate[] }> {
  const searchParams = new URLSearchParams();
  if (params?.category) searchParams.set('category', params.category);
  if (params?.is_featured !== undefined) searchParams.set('is_featured', params.is_featured.toString());

  const query = searchParams.toString();
  return apiRequest(`/api/forms/templates${query ? `?${query}` : ''}`);
}

/**
 * Get a single form template
 */
export async function getFormTemplate(templateId: string): Promise<FormTemplate> {
  return apiRequest(`/api/forms/templates/${templateId}`);
}

/**
 * Create a form from a template
 */
export async function createFormFromTemplate(
  templateId: string,
  overrides?: { title?: string; folder_id?: string }
): Promise<Form> {
  return apiRequest(`/api/forms/templates/${templateId}/use`, {
    method: 'POST',
    body: JSON.stringify(overrides || {}),
  });
}

// =============================================================================
// Column Schema API (Schema-First Column Management)
// =============================================================================

import type {
  ColumnDefinition,
  CreateColumnRequest,
  UpdateColumnRequest,
  RunColumnRequest,
  RunColumnResult,
  ColumnRunHistory,
  UpdatePreferencesRequest,
  ColumnsResponse,
} from '@/types/columns';

/**
 * List all column definitions
 */
export async function getColumns(params?: {
  include_archived?: boolean;
}): Promise<ColumnsResponse> {
  const searchParams = new URLSearchParams();
  if (params?.include_archived) searchParams.set('include_archived', 'true');

  const query = searchParams.toString();
  return apiRequest(`/api/columns${query ? `?${query}` : ''}`);
}

/**
 * Create a new column definition
 */
export async function createColumn(request: CreateColumnRequest): Promise<ColumnDefinition> {
  return apiRequest('/api/columns', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Update an existing column definition
 */
export async function updateColumn(
  columnId: string,
  request: UpdateColumnRequest
): Promise<ColumnDefinition> {
  return apiRequest(`/api/columns/${columnId}`, {
    method: 'PATCH',
    body: JSON.stringify(request),
  });
}

/**
 * Delete (archive) a column definition
 */
export async function deleteColumn(columnId: string): Promise<{ message: string }> {
  return apiRequest(`/api/columns/${columnId}`, {
    method: 'DELETE',
  });
}

/**
 * Run a computed column on contacts
 */
export async function runColumn(
  columnId: string,
  request?: RunColumnRequest
): Promise<RunColumnResult> {
  return apiRequest(`/api/columns/${columnId}/run`, {
    method: 'POST',
    body: JSON.stringify(request || {}),
  });
}

/**
 * Get run history for a column
 */
export async function getColumnRunHistory(
  columnId: string,
  limit = 10
): Promise<{ history: ColumnRunHistory[] }> {
  return apiRequest(`/api/columns/${columnId}/history?limit=${limit}`);
}

/**
 * Update user column preferences (visibility, order, width)
 */
export async function updateColumnPreferences(
  request: UpdatePreferencesRequest
): Promise<{ message: string }> {
  return apiRequest('/api/columns/preferences', {
    method: 'PATCH',
    body: JSON.stringify(request),
  });
}

// =============================================================================
// Calendar API
// =============================================================================

import type {
  CalendarEventsResponse,
  CalendarUsersResponse,
  CalendarEvent,
  UserColorsResponse,
} from '@/types/calendar';

/**
 * Get calendar events for a date range
 */
export async function getCalendarEvents(
  startDate: string,
  endDate: string,
  userIds?: string[]
): Promise<CalendarEventsResponse> {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
  });
  if (userIds && userIds.length > 0) {
    params.set('user_ids', userIds.join(','));
  }
  return apiRequest(`/api/calendar/events?${params}`);
}

/**
 * Get calendar users with sync status
 */
export async function getCalendarUsers(): Promise<CalendarUsersResponse> {
  return apiRequest('/api/calendar/users');
}

/**
 * Get a single calendar event by ID
 */
export async function getCalendarEvent(eventId: string): Promise<CalendarEvent> {
  return apiRequest(`/api/calendar/events/${eventId}`);
}

/**
 * Get user color assignments for calendar
 */
export async function getCalendarUserColors(): Promise<UserColorsResponse> {
  return apiRequest('/api/calendar/user-colors');
}

// =============================================================================
// Calendar Admin API
// =============================================================================

export interface CalendarVisibilityUser {
  id: string;
  email: string;
  name: string | null;
  calendar_visibility: 'public' | 'team' | 'private';
}

export interface CalendarVisibilityResponse {
  users: CalendarVisibilityUser[];
}

export interface CalendarPermission {
  id: string;
  viewer_user_id: string | null;
  viewer_role: string | null;
  calendar_owner_id: string;
  calendar_owner_email: string;
  calendar_owner_name: string | null;
  can_view: boolean;
  can_view_details: boolean;
  created_at: string;
}

export interface CalendarPermissionsResponse {
  permissions: CalendarPermission[];
}

/**
 * Get all users' calendar visibility settings (admin only)
 */
export async function getCalendarVisibilitySettings(): Promise<CalendarVisibilityResponse> {
  return apiRequest('/api/calendar/admin/visibility');
}

/**
 * Update a user's calendar visibility setting (admin only)
 */
export async function updateCalendarVisibility(
  userId: string,
  visibility: 'public' | 'team' | 'private'
): Promise<{ success: boolean; user_id: string; visibility: string }> {
  return apiRequest(`/api/calendar/admin/visibility/${userId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ calendar_visibility: visibility }),
  });
}

/**
 * Get all calendar permission grants (admin only)
 */
export async function getCalendarPermissions(): Promise<CalendarPermissionsResponse> {
  return apiRequest('/api/calendar/admin/permissions');
}

/**
 * Grant calendar viewing permission (admin only)
 */
export async function grantCalendarPermission(
  calendarOwnerId: string,
  params: {
    viewer_user_id?: string;
    viewer_role?: string;
    can_view?: boolean;
    can_view_details?: boolean;
  }
): Promise<{ success: boolean; permission: CalendarPermission | null }> {
  return apiRequest(`/api/calendar/admin/permissions/${calendarOwnerId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
}

/**
 * Revoke a calendar permission (admin only)
 */
export async function revokeCalendarPermission(
  permissionId: string
): Promise<{ success: boolean; deleted: boolean }> {
  return apiRequest(`/api/calendar/admin/permissions/${permissionId}`, {
    method: 'DELETE',
  });
}

// =============================================================================
// Campaign Templates & Wizard API
// =============================================================================

export async function fetchCampaignTemplates(
  platform?: string,
  clientSlug?: string
): Promise<{ templates: any[] }> {
  const params = new URLSearchParams();
  if (platform) params.set('platform', platform);
  if (clientSlug) params.set('client_slug', clientSlug);
  const qs = params.toString();
  return apiRequest(`/api/campaigns/templates${qs ? `?${qs}` : ''}`);
}

export async function createCampaignTemplate(
  data: { name: string; platform: string; client_slug: string | null; config: Record<string, unknown> }
): Promise<any> {
  return apiRequest('/api/campaigns/templates', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function fetchCampaignTemplate(id: string): Promise<any> {
  return apiRequest(`/api/campaigns/templates/${id}`);
}

export async function deleteCampaignTemplate(id: string): Promise<void> {
  return apiRequest(`/api/campaigns/templates/${id}`, {
    method: 'DELETE',
  });
}

export async function createSearchCampaign(
  data: { client_slug: string; customer_id?: string; spec: Record<string, unknown>; dry_run?: boolean }
): Promise<any> {
  return apiRequest('/api/google-ads/campaigns/create', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function createShoppingCampaign(
  data: { client_slug: string; customer_id?: string; spec: Record<string, unknown>; dry_run?: boolean }
): Promise<any> {
  return apiRequest('/api/google-ads/campaigns/create-shopping', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function fetchClientAdContext(
  clientSlug: string
): Promise<any> {
  return apiRequest(`/api/agency/clients/${clientSlug}/ad-context`);
}

// =============================================================================
// Keyword Research & Budget Forecasting
// =============================================================================

import type {
  KeywordSearchRequest,
  KeywordSearchResponse,
  GeographyResult,
  ResearchSession,
  KeywordResult,
  CreateSessionRequest,
  UpdateSessionRequest,
  ForecastRequest,
  ForecastResponse,
  ForecastTier,
} from '@/types/keyword-research';

/**
 * Search keywords using blended Google KP + DataForSEO pipeline
 */
export async function searchKeywords(
  request: KeywordSearchRequest
): Promise<KeywordSearchResponse> {
  return apiRequest('/api/keyword-research/search', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Resolve a natural language geography query to Google Ads geo target constants.
 * Debounce hint: call with 300ms debounce from component.
 */
export async function resolveGeography(
  query: string,
  countryCode?: string
): Promise<GeographyResult[]> {
  const params = new URLSearchParams({ query });
  if (countryCode) params.set('country_code', countryCode);
  return apiRequest(`/api/keyword-research/geography?${params}`);
}

/**
 * Create a new keyword research session for a client
 */
export async function createKeywordSession(
  request: CreateSessionRequest
): Promise<ResearchSession> {
  return apiRequest('/api/keyword-research/sessions', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Get a keyword research session with its results
 */
export async function getKeywordSession(
  sessionId: string
): Promise<{ session: ResearchSession; results: KeywordResult[] }> {
  return apiRequest(`/api/keyword-research/sessions/${sessionId}`);
}

/**
 * List keyword research sessions for a client
 */
export async function getClientKeywordSessions(
  clientSlug: string,
  limit?: number
): Promise<ResearchSession[]> {
  const params = new URLSearchParams();
  if (limit) params.set('limit', String(limit));
  const query = params.toString();
  return apiRequest(`/api/keyword-research/sessions/client/${clientSlug}${query ? `?${query}` : ''}`);
}

/**
 * Update a keyword research session
 */
export async function updateKeywordSession(
  sessionId: string,
  updates: UpdateSessionRequest
): Promise<ResearchSession> {
  return apiRequest(`/api/keyword-research/sessions/${sessionId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

/**
 * Delete a keyword research session
 */
export async function deleteKeywordSession(
  sessionId: string
): Promise<void> {
  await apiRequest(`/api/keyword-research/sessions/${sessionId}`, {
    method: 'DELETE',
  });
}

/**
 * Generate budget forecast for a keyword research session
 */
export async function generateForecast(
  request: ForecastRequest
): Promise<ForecastResponse> {
  return apiRequest('/api/keyword-research/forecast', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Get cached forecasts for a session
 */
export async function getSessionForecasts(
  sessionId: string
): Promise<ForecastTier[]> {
  return apiRequest(`/api/keyword-research/forecast/${sessionId}`);
}

/**
 * Export keyword data as CSV or PDF
 */
export async function exportKeywordData(
  sessionId: string,
  format: 'csv' | 'pdf',
  includeForecast: boolean
): Promise<Blob> {
  const params = new URLSearchParams({
    format,
    include_forecast: String(includeForecast),
  });
  const headers = await getAuthHeaders();
  const response = await fetch(
    `${config.apiUrl}/api/keyword-research/export/${sessionId}?${params}`,
    { headers, credentials: 'include' }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Export failed' }));
    throw new Error(error.detail || error.error || `Export error: ${response.status}`);
  }
  return response.blob();
}

// =============================================================================
// Ad Library — Competitive Intelligence
// =============================================================================

import type {
  TrackingProfile,
  ProfileTemplate,
  CompetitorAd,
  AdListFilters,
  InspirationBoard,
  AnalysisSnapshot,
  CreativeRecommendation,
  ScrapeHealth,
  AdLibraryStats,
  PaginatedAds,
  CompetitorSuggestion,
  CrossProfileAnalysis,
  AnalysisTimelineEntry,
  ProfileThreat,
} from '@/types/ad-library';

// --- Profiles ---

export async function fetchAdLibraryProfiles(params?: {
  client_id?: string;
  include_shared?: boolean;
  tags?: string[];
}): Promise<TrackingProfile[]> {
  const qs = new URLSearchParams();
  if (params?.client_id) qs.set('client_id', params.client_id);
  if (params?.include_shared !== undefined) qs.set('include_shared', String(params.include_shared));
  if (params?.tags?.length) qs.set('tags', params.tags.join(','));
  const query = qs.toString();
  return apiRequest(`/api/ad-library/profiles${query ? `?${query}` : ''}`);
}

export async function fetchAdLibraryProfile(profileId: string): Promise<TrackingProfile> {
  return apiRequest(`/api/ad-library/profiles/${profileId}`);
}

export async function createAdLibraryProfile(data: Partial<TrackingProfile>): Promise<TrackingProfile> {
  return apiRequest('/api/ad-library/profiles', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAdLibraryProfile(
  profileId: string,
  data: Partial<TrackingProfile>
): Promise<TrackingProfile> {
  return apiRequest(`/api/ad-library/profiles/${profileId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteAdLibraryProfile(profileId: string): Promise<void> {
  return apiRequest(`/api/ad-library/profiles/${profileId}`, {
    method: 'DELETE',
  });
}

export async function toggleAdLibraryProfile(
  profileId: string
): Promise<{ is_active: boolean }> {
  return apiRequest(`/api/ad-library/profiles/${profileId}/toggle`, {
    method: 'POST',
  });
}

export async function triggerProfileScrape(
  profileId: string
): Promise<{ status: string; run_id: string }> {
  return apiRequest(`/api/ad-library/profiles/${profileId}/scrape`, {
    method: 'POST',
  });
}

export async function fetchProfileSuggestions(
  profileId: string
): Promise<CompetitorSuggestion[]> {
  return apiRequest(`/api/ad-library/profiles/${profileId}/suggestions`);
}

// --- Templates ---

export async function fetchAdLibraryTemplates(): Promise<ProfileTemplate[]> {
  return apiRequest('/api/ad-library/templates');
}

export async function createAdLibraryTemplate(
  data: Partial<ProfileTemplate>
): Promise<ProfileTemplate> {
  return apiRequest('/api/ad-library/templates', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function createProfileFromTemplate(
  templateId: string,
  data: { name: string; client_id?: string; description?: string }
): Promise<TrackingProfile> {
  return apiRequest(`/api/ad-library/templates/${templateId}/create-profile`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// --- Ads ---

export async function fetchAdLibraryAds(filters: AdListFilters): Promise<PaginatedAds> {
  const qs = new URLSearchParams();
  if (filters.profile_id) qs.set('profile_id', filters.profile_id);
  if (filters.page_ids?.length) qs.set('page_ids', filters.page_ids.join(','));
  if (filters.date_from) qs.set('date_from', filters.date_from);
  if (filters.date_to) qs.set('date_to', filters.date_to);
  if (filters.spend_min !== undefined) qs.set('spend_min', String(filters.spend_min));
  if (filters.spend_max !== undefined) qs.set('spend_max', String(filters.spend_max));
  if (filters.ad_format) qs.set('ad_format', filters.ad_format);
  if (filters.status) qs.set('status', filters.status);
  if (filters.ai_analyzed !== undefined) qs.set('ai_analyzed', String(filters.ai_analyzed));
  if (filters.page !== undefined) qs.set('page', String(filters.page));
  if (filters.page_size !== undefined) qs.set('page_size', String(filters.page_size));
  const query = qs.toString();
  return apiRequest(`/api/ad-library/ads${query ? `?${query}` : ''}`);
}

export async function fetchAdLibraryAd(adId: string): Promise<CompetitorAd> {
  return apiRequest(`/api/ad-library/ads/${adId}`);
}

export async function bulkAnalyzeAds(adIds: string[]): Promise<{ status: string; count: number }> {
  return apiRequest('/api/ad-library/ads/bulk-analyze', {
    method: 'POST',
    body: JSON.stringify({ ad_ids: adIds }),
  });
}

// --- Boards ---

export async function fetchAdLibraryBoards(clientId?: string): Promise<InspirationBoard[]> {
  const qs = clientId ? `?client_id=${clientId}` : '';
  return apiRequest(`/api/ad-library/boards${qs}`);
}

export async function createAdLibraryBoard(
  data: { name: string; description?: string; client_id?: string }
): Promise<InspirationBoard> {
  return apiRequest('/api/ad-library/boards', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function fetchBoardAds(
  boardId: string,
  page?: number,
  pageSize?: number
): Promise<PaginatedAds> {
  const qs = new URLSearchParams();
  if (page !== undefined) qs.set('page', String(page));
  if (pageSize !== undefined) qs.set('page_size', String(pageSize));
  const query = qs.toString();
  return apiRequest(`/api/ad-library/boards/${boardId}/ads${query ? `?${query}` : ''}`);
}

export async function addAdsToBoard(
  boardId: string,
  adIds: string[]
): Promise<{ status: string; count: number }> {
  return apiRequest(`/api/ad-library/boards/${boardId}/ads`, {
    method: 'POST',
    body: JSON.stringify({ ad_ids: adIds }),
  });
}

export async function removeAdsFromBoard(
  boardId: string,
  adIds: string[]
): Promise<{ status: string }> {
  return apiRequest(`/api/ad-library/boards/${boardId}/ads`, {
    method: 'DELETE',
    body: JSON.stringify({ ad_ids: adIds }),
  });
}

// --- Analysis ---

export async function fetchProfileAnalysis(
  profileId: string
): Promise<AnalysisSnapshot | null> {
  return apiRequest(`/api/ad-library/analysis/${profileId}`);
}

export async function triggerProfileAnalysis(
  profileId: string
): Promise<{ status: string }> {
  return apiRequest(`/api/ad-library/analysis/${profileId}`, {
    method: 'POST',
  });
}

export async function fetchProfileRecommendations(
  profileId: string
): Promise<CreativeRecommendation[]> {
  return apiRequest(`/api/ad-library/analysis/${profileId}/recommendations`);
}

export async function pushToCreativeStudio(
  profileId: string,
  recommendationIdx: number
): Promise<{ status: string; prefill: Record<string, any> }> {
  return apiRequest(`/api/ad-library/analysis/${profileId}/push-to-studio`, {
    method: 'POST',
    body: JSON.stringify({ recommendation_index: recommendationIdx }),
  });
}

export async function fetchCrossProfileAnalysis(
  clientId?: string
): Promise<CrossProfileAnalysis | null> {
  const qs = clientId ? `?client_id=${clientId}` : '';
  return apiRequest(`/api/ad-library/analysis/cross-profile${qs}`);
}

export async function triggerCrossProfileAnalysis(): Promise<{ status: string }> {
  return apiRequest('/api/ad-library/analysis/cross-profile', {
    method: 'POST',
  });
}

export async function fetchProfileReport(profileId: string): Promise<Blob> {
  const headers = await getAuthHeaders();
  const response = await fetch(
    `${config.apiUrl}/api/ad-library/analysis/${profileId}/report`,
    { headers, credentials: 'include' }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Report failed' }));
    throw new Error(error.detail || error.error || `Report error: ${response.status}`);
  }
  return response.blob();
}

export async function fetchProfileThreats(
  profileId: string
): Promise<ProfileThreat[]> {
  return apiRequest(`/api/ad-library/analysis/${profileId}/threats`);
}

export async function fetchAnalysisTimeline(
  profileId: string
): Promise<AnalysisTimelineEntry[]> {
  return apiRequest(`/api/ad-library/analysis/${profileId}/timeline`);
}

// --- Health & Stats ---

export async function fetchScrapeHealth(): Promise<ScrapeHealth> {
  return apiRequest('/api/ad-library/health');
}

export async function fetchAdLibraryStats(): Promise<AdLibraryStats> {
  return apiRequest('/api/ad-library/stats');
}
